// eslint-disable-next-line import/no-extraneous-dependencies
import '@mux/mux-player/themes/minimal';
import '../../styles/muxPlayerClipStyles.css';

import MuxPlayer from '@mux/mux-player-react';
import * as EventTracker from '@src/analytics/EventTracker';
import { SecondaryButton } from '@src/components/common/dialogStyles';
import { CLIP_PREVIEW_Z } from '@src/components/content-components/clipPreviewZ';
import { useShortcutScope } from '@src/hooks/shortcuts/useShortcutScope';
import { useEnhancementPublishGate } from '@src/hooks/useEnhancementPublishGate';
import {
  useHandOffPreviewClip,
  useNavigateToCreatorStudio,
} from '@src/hooks/useNavigateToCreatorStudio';
import { usePlayerVolume } from '@src/hooks/usePlayerVolume';
import { SAFE_ZONE_STRINGS } from '@src/safe-zones/strings';
import {
  type SafeZoneSelection,
  type SafeZoneVariantKey,
  splitSafeZoneSelection,
} from '@src/safe-zones/types';
import { RenameClipService } from '@src/services/clip/RenameClipService';
import { deriveClipPublishState } from '@src/services/publish/clipCardPublishState';
import { deriveReelSections } from '@src/services/reel/deriveReelSections';
import {
  isEditableElementFocused,
  isModalDialogOpen,
} from '@src/services/shortcuts/keyboard';
import { isTempClipId } from '@src/services/WriteQueueService';
import { StoreContext } from '@src/stores-v2/RootStoreContext';
import {
  useClipPreview,
  useContentStudioUI,
  useDatoClient,
  usePublishRecordsStore,
  useStore,
  useTranscription,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { DEFAULT_INK } from '@src/utility/annotationInk';
import { isClipAwaitingExpansion } from '@src/utility/clipPublishReadiness';
import { formatDateTimeCompact } from '@src/utility/dateFormat';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import { LogManager } from '@src/utility/Logger';
import { assignDistinctPersonColors } from '@src/utility/speakerUtils';
import { dispatchTranscriptIndexJump } from '@src/utility/transcriptEvents';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';
import { v4 as uuid } from 'uuid';

import CloseIcon from '../../svgs/CloseIcon';
import CropIcon from '../../svgs/CropIcon';
import PencilIcon from '../../svgs/PencilIcon';
import ThumbnailIcon from '../../svgs/ThumbnailIcon';
import { AspectRatio } from '../../types.ts/video';
import {
  createImageElement,
  getAvailableClipFormats,
  getPosterUrl,
  getRandomFileName,
} from '../../utility/general';
import { getFormattedDurationForStoryVideo } from '../../utility/timeFormat';
import { useFlagsCombination } from '../../utility/useFlagsCombination';
import { resolveClipSharedContents } from '../../utility/videoClip';
import { AutosaveStatusIcon } from '../common/AutosaveStatusIcon';
import { AutosaveStatusIndicator } from '../common/AutosaveStatusIndicator';
import { ClipTypeBadge as ClipTypeBadgeBase } from '../common/ClipTypeBadge';
import CaptionsFloatingToolbar from '../common/floatingToolbar/CaptionsFloatingToolbar';
import { FloatingToolbarHostProvider } from '../common/floatingToolbar/FloatingToolbarHost';
import ImageFloatingToolbar from '../common/floatingToolbar/ImageFloatingToolbar';
import TextFloatingToolbar from '../common/floatingToolbar/TextFloatingToolbar';
import VideoFloatingToolbar from '../common/floatingToolbar/VideoFloatingToolbar';
import MuxPlayerClip from '../common/MuxPlayerClip';
import MuxPlayerMultiClip from '../common/MuxPlayerMultiClip';
import { NativeLocalVideoPreview } from '../common/NativeLocalVideoPreview';
import PhotoModalTop from '../common/PhotoModalTop';
import PhotoModal from '../common/PhotosModal';
import {
  PlayerSettingsSlot,
  PlayerSettingsSlotProvider,
} from '../common/playerSettingsSlot';
import ShareableImageView from '../common/ShareableImageView';
import SocialProfileRefreshOverlay from '../common/SocialProfileRefreshOverlay';
import { LoadingOverlay, LoadingState } from '../common/Spinner';
import { COMPACT_TOOLTIP_STYLE } from '../common/tooltipStyles';
import Modal from '../Modal';
import { SafeZoneOverlay } from '../stage/safe-zones/SafeZoneOverlay';
import { useTimelineMarkers } from '../transcript/hooks/useTimelineMarkers';
import { ClipAnnotationLayer } from './annotations/ClipAnnotationLayer';
import { useClipAnnotations } from './annotations/useClipAnnotations';
import { cardSpansFromMarkers } from './clipCardSpans';
import ClipDimensionsDropdown from './ClipDimensionsDropdown';
import { ClipPhotoReplacementModal } from './ClipPhotoReplacementModal';
import ClipPreviewControlBar from './ClipPreviewControlBar';
import { ClipPreviewReframeModal } from './ClipPreviewReframeModal';
import { ClipPreviewTitle } from './ClipPreviewTitle';
import { useClipPreviewToolbarHost } from './clipPreviewToolbarHost';
import {
  computeClipPreviewSearch,
  parseSafeZone,
  serializeSafeZone,
} from './clipPreviewUrlState';
import ClipPublishShareButton from './ClipPublishShareButton';
import { ClipTemplatesProvider } from './ClipTemplatesProvider';
import { ClipTranscriptBridge } from './ClipTranscriptBridge';
import { ClipVersionHistoryPanel } from './ClipVersionHistoryPanel';
import {
  ClipVersionPreviewGuard,
  ClipVersionPreviewTag,
} from './ClipVersionPreviewChrome';
import { CommentComposer } from './comments/CommentComposer';
import { CommentList } from './comments/CommentList';
import { toMentionPeople } from './comments/useMentionAutocomplete';
import { CompositionCanvasActionsLayer } from './CompositionCanvasActionsLayer';
import CreatomateClipPreview from './CreatomateClipPreview';
import { type DimensionItem } from './DimensionsSelector';
import { DownloadsTab } from './DownloadsTab';
import { ExportMenu } from './ExportMenu';
import { createClipRootProxy } from './hooks/clipTranscriptProxies';
import { useClipAssignees } from './hooks/useClipAssignees';
import { useClipAudioFirstCanvas } from './hooks/useClipAudioFirstCanvas';
import { useClipAutosaveStatus } from './hooks/useClipAutosaveStatus';
import { useClipEditSession } from './hooks/useClipEditSession';
import { useClipPreviewRenderer } from './hooks/useClipPreviewRenderer';
import { useClipResourceLoader } from './hooks/useClipResourceLoader';
import { useCommentStore } from './hooks/useCommentStore';
import { usePlaybackState } from './hooks/usePlaybackState';
import { usePublishedClipSource } from './hooks/usePublishedClipSource';
import {
  cssAspectRatio,
  useRenderedAspectRatio,
} from './hooks/useRenderedAspectRatio';
import { useRendererPlayback } from './hooks/useRendererPlayback';
import { useRenderStatusObserver } from './hooks/useRenderStatusObserver';
import { useStoryClipPublishRecords } from './hooks/useStoryClipPublishRecords';
import LastPublishedInfo from './LastPublishedInfo';
import { PublishProgressHeaderStrip } from './PublishProgressHeaderStrip';
import { ReelEditorPanel } from './ReelEditor/ReelEditorPanel';
import { useReelAutoName } from './ReelEditor/useReelAutoName';
import { useStoryTopicSpans } from './ReelEditor/useStoryTopicSpans';
import { useTrackReelEditorOpened } from './ReelEditor/useTrackReelEditorOpened';
import SafeZoneDropdown from './SafeZoneDropdown';
import { ShareModalPostsContent } from './ShareModalPostsContent';
import {
  initialShareModalTab,
  parseClipTab,
  resolveVisibleClipTab,
  type ShareModalTab as ShareModalTabType,
  ShareModalTabs,
  shareModalTabVisibility,
} from './ShareModalTabs';
import SocialShareStatus from './SocialShareStatus';
import { useSaveClipsAsReel } from './useSaveClipsAsReel';

import type { ImageKey, ImageWithType } from '../../types.ts/general';
import type {
  Artifact,
  AssociatedVideo,
  ClipMetadata,
  FileData,
  Video,
  VideoClip,
  VideoLastPublish,
} from '../../types.ts/story';
import type { Cut } from '../common/MuxPlayerMultiClip';
import type { ClipCommentMarker } from './ClipCommentMarkers';
import type { CompositionCanvasQuickEditHandle } from './CompositionCanvasActionsLayer';
import type MuxPlayerElement from '@mux/mux-player';
import type { MuxPlayerElementEventMap } from '@mux/mux-player';
import type { PlaybackBridge } from '@src/hooks/PlaybackBridge';
import type { ScopeRegistration } from '@src/services/shortcuts/types';
import type { Comment } from '@src/types.ts/comment';
import type { CSSProperties } from 'styled-components';

const logger = LogManager.getLogger('SocialClipShareModal');

const playerStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100%',
  height: '100%',
  borderRadius: 10,
  overflow: 'hidden',
  border: '1px solid var(--border)',
  '--controls': 'none',
} as CSSProperties;

const formatPlaybackTime = (time: number) => {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;
};

// Temporarily hide the duration badge (e.g. "03:00") in the clip-preview modal.
// Flip to `true` to restore it.
const SHOW_DURATION_BADGE = false;

const getDurationColor = (duration: number | null) => {
  if (!duration) return;
  if (duration <= 15) {
    return '#CBD7F0';
  } else if (duration <= 30) {
    return '#7879f1';
  } else if (duration <= 60) {
    return '#F178B6';
  } else {
    return '#484848';
  }
};

const getClipThumbnail = (activeClip: VideoClip | AssociatedVideo) => {
  // Only trust the active variant's own stored thumbnail when it has a
  // rendered video (videoFilePrimary). This guarantees the thumbnail was
  // generated at the correct AR. All other cases — unpublished variants,
  // variants without thumbnails, cross-AR fallbacks — risk showing a
  // thumbnail from a different AR that appears stretched.
  //
  // Returning empty lets the effectivePosterUrl chain fall through to:
  //   1. playbackId-based Mux thumbnail (correct AR for published variants)
  //   2. No poster at all (for drafts — the live renderer loads directly)
  if (activeClip.thumbnail?.url && activeClip.videoFilePrimary) {
    return activeClip.thumbnail.url;
  }

  return '';
};

type Props = {
  onClose: () => void;
  clip: VideoClip & {
    autoPlay?: boolean;
    videoJson?: Video['videoSource'];
    videoSource?: Video['videoSource'];
    selectedDimension?: string;
  };
  /** Optimistic comment-count delta for a video (post → +1, delete → −1). */
  onCommentCountChange?: (videoId: string, delta: number) => void;
};

/**
 * Full functional modal content — only mounted when the clip has a real
 * (non-temp) ID. Contains all the heavy hooks (useClipPreviewRenderer,
 * useClipEditSession, useClipResourceLoader) and interactive UI.
 */
const FullClipModalContent = observer(
  ({
    clip,
    savingRef,
    beforeCloseRef,
    onSavingChange,
    onCommentCountChange,
  }: {
    clip: Props['clip'];
    savingRef?: React.MutableRefObject<boolean>;
    beforeCloseRef?: React.MutableRefObject<(() => Promise<void>) | null>;
    onSavingChange?: (saving: boolean) => void;
    onCommentCountChange?: (videoId: string, delta: number) => void;
  }) => {
    const videoCreator = useVideoCreatorStore();
    const userIdentity = useUserIdentity();
    const datoClientStore = useDatoClient();

    const story = videoCreator?.story;
    const { clipJson } = clip;
    // `clip` is whatever was assigned to `videoClipPreview`, which is not always
    // a STORY_QUERY clip — see resolveClipSharedContents.
    const clipSharedContents = useMemo(
      () => resolveClipSharedContents(clip, story?.otherVideos),
      [clip, story?.otherVideos],
    );
    const durationData = getFormattedDurationForStoryVideo(clip);
    const navigate = useNavigateToCreatorStudio();
    const handOffPreviewClip = useHandOffPreviewClip();

    const [showPhotosModal, togglePhotosModal] = useState<boolean>(false);
    // PLAT-11877 — the Languages tab's pane, filled by the transcript bridge.
    const [languagesPane, setLanguagesPane] = useState<HTMLDivElement | null>(
      null,
    );
    const [selectedImage, setSelectedImage] = useState<
      | (ImageWithType[ImageKey] & {
          quote?: string;
          shareableImageId?: string;
        })
      | null
    >(null);

    const [savingStock, setSavingStock] = useState<boolean>(false);
    const [savingQuote, setSavingQuote] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>('');
    // PLAT-10948: one consolidated effect below writes clipId / clipTab /
    // clipLang / clipPlayback / clipSafeZone, so a link reproduces what the user
    // sees. `clipTranslationReview` is the bridge's — see `ClipTranscriptBridge`.
    const [searchParams, setSearchParams] = useSearchParams();
    // Session store owns clip data, selectedDimension, previewMode, and
    // creatingDimension. Deep-clones the clip on construction to sever all
    // MobX reference chains (replaces the old `detachClip` pattern). Language
    // and preview mode are seeded once from the URL so a shared/refreshed link
    // opens on the same language + preview mode.
    // Only seed an overlay when the link carries one — an absent param must fall
    // through to the singleton "last used" value, not force 'none'.
    const urlSafeZone = searchParams.has('clipSafeZone')
      ? parseSafeZone(searchParams.get('clipSafeZone'))
      : null;
    const sessionStore = useClipEditSession(clip, {
      initialLanguage: searchParams.get('clipLang'),
      initialPublishRecordId: searchParams.get('clipPublishRecord'),
      initialPreviewMode:
        searchParams.get('clipPlayback') === 'rendered'
          ? 'rendered'
          : searchParams.get('clipPlayback') === 'live'
            ? 'live'
            : undefined,
      initialSafeZone: urlSafeZone?.selection,
      initialSafeZoneVariants: urlSafeZone?.variants,
    });
    const clipPreviewStore = useClipPreview();
    const selectedDimension = sessionStore.selectedDimension;
    // Each aspect-ratio format is its own Video record, so key the comment store
    // on the selected variant's record id — switching dimension loads a fresh
    // thread. `undefined` for an unsaved variant (the store skips loading).
    const activeVideoId = sessionStore.activeVariant?.id;
    const commentStore = useCommentStore(activeVideoId, story?.id);
    // Workspace teammates for the comment @-mention autocomplete (resolved here,
    // where the store providers exist, and passed into the comment components).
    const clipAssignees = useClipAssignees();
    const mentionPeople = useMemo(
      () => toMentionPeople(clipAssignees),
      [clipAssignees],
    );

    // Report comment-count changes to the clips list so the card indicator
    // updates immediately (post → +1, delete → −1) instead of waiting for the
    // list's next refetch. Baseline is (re)set after a load settles — and on
    // video switch — so the initial load and AR changes don't emit a bogus
    // delta; only user edits while not loading do.
    const commentCount = commentStore.commentCount;
    const commentsLoading = commentStore.isLoading;
    const commentBaselineRef = useRef<number | null>(null);
    useEffect(() => {
      commentBaselineRef.current = null;
    }, [activeVideoId]);
    useEffect(() => {
      if (commentsLoading) return;
      const baseline = commentBaselineRef.current;
      commentBaselineRef.current = commentCount;
      if (baseline !== null && commentCount !== baseline && activeVideoId) {
        onCommentCountChange?.(activeVideoId, commentCount - baseline);
      }
    }, [commentCount, commentsLoading, onCommentCountChange, activeVideoId]);

    // Collision-free roster avatar colors — built once here and handed to every
    // comment surface (markers, list, @-mention popovers) so two people never
    // share a hue and the same person keeps one color across all of them.
    const personColors = useMemo(
      () => assignDistinctPersonColors(mentionPeople.map(p => p.name)),
      [mentionPeople],
    );

    // Pinned top-level comments reduced to marker data for the control bar
    // (avatar + hover card beneath the video). `#index` matches list order.
    /**
     * Clicking a scrubber dot behaves like the timecode chip in the list: reveal
     * the conversation, and paint that comment's marks if it has any
     * (PLAT-11291). Without this, jumping between drawings meant going back to
     * the list for every one.
     *
     * Unconditional rather than only-while-a-drawing-is-shown: the chip does not
     * care either, drawings paint only while paused, and a dot click pauses. A
     * comment with no marks simply clears whatever was on the frame, so the frame
     * always matches the comment you landed on.
     */
    const handleCommentJump = useCallback(
      (commentId?: string) => {
        setActiveTab('comments');
        if (!commentId) return;
        const target = commentStore.threads.find(
          thread => thread.top.id === commentId,
        )?.top;
        setShownDrawing(target?.drawing ? target : null);
      },
      [commentStore],
    );

    const commentMarkers = useMemo<ClipCommentMarker[]>(
      () =>
        commentStore.threads.flatMap((t, i) => {
          const time = t.top.videoTimeSeconds;
          if (time == null) return [];
          return [
            {
              id: t.top.id,
              time,
              index: i + 1,
              authorName: t.top.authorName,
              authorId: t.top.authorId,
              authorColor: personColors.get(t.top.authorName),
              body: t.top.body,
              createdAt: t.top.createdAt,
              isResolved: t.isResolved,
              // Dragging a marker rewrites what the note points at, so it is
              // author-only — the same gate as editing and deleting.
              canMove: commentStore.isOwnComment(t.top),
            },
          ];
        }),
      [commentStore.threads, personColors, commentStore],
    );

    // Commit a dragged marker's new position. `refresh` is not needed: the
    // store's optimistic update already re-derives `commentMarkers`.
    const handleCommentMove = useCallback(
      (id: string, time: number) => {
        void commentStore.setCommentTime(id, time, 'drag');
      },
      [commentStore],
    );

    const previewMode = sessionStore.previewMode;
    const {
      contentStudioEnableClipShare,
      contentStudioModalEnableShare,
      enableVideoClipEdit,
      enableSharing,
      contentStudioModalEnableThumbnail,
      contentStudioModalEnableAnalytics,
      contentStudioModalEnableTranscript,
      contentStudioModalEnableComments,
      contentStudioEnableCommentDrawing,
      contentStudioModalEnableScenesStrip,
      contentStudioModalEnableScenesStripInPlayer,
      enableBasicReelCreation,
      enableCreatorStudioNav,
      enableSafeZones,
      enableCompositionCanvasQuickEdit,
      enableTranslationReviewerMode,
      enableClipVersionHistory,
      initialized: flagsInitialized,
    } = useFlagsCombination(userIdentity.currentRole);
    const contentStudioUI = useContentStudioUI();
    // Tab visibility derived from the flags once, then used for BOTH the tab-row
    // props below and the `?clipTab` reconcile, so the two can't drift.
    const tabVisibility = useMemo(
      () =>
        shareModalTabVisibility({
          enableShare: contentStudioModalEnableShare,
          enableTranscript: contentStudioModalEnableTranscript,
          enableComments: contentStudioModalEnableComments,
          enableReelEditor: enableBasicReelCreation,
          enableHistory: enableClipVersionHistory,
          reviewerMode: enableTranslationReviewerMode,
        }),
      [
        contentStudioModalEnableShare,
        contentStudioModalEnableTranscript,
        contentStudioModalEnableComments,
        enableBasicReelCreation,
        enableClipVersionHistory,
        enableTranslationReviewerMode,
      ],
    );
    // `?clipTab` (shared/refreshed link) > a one-shot request from the card (the
    // comment badge) > a reel's own structure > the default tab — all resolved
    // against `tabVisibility`, so the seed can't land on a tab the row won't
    // render. Without that a `?clipTab=posts` link would show for one frame
    // before the reconcile below corrected it.
    const [activeTab, setActiveTab] = useState<ShareModalTabType>(() =>
      initialShareModalTab({
        fromUrl: parseClipTab(searchParams.get('clipTab')),
        requestedTab: contentStudioUI.requestedClipPreviewTab,
        isReel: !!clipJson?.isReel,
        visibility: tabVisibility,
      }),
    );
    useEffect(() => {
      contentStudioUI.clearRequestedClipPreviewTab();
    }, [contentStudioUI]);

    // PLAT-10948 — `?clipTab` is validated against the tab union, not
    // against the flags, so a shared link can name a tab this user can't see:
    // the pane content is flag-gated while `Tabs` honors any controlled id, so
    // that renders an empty right pane with no tab selected. Reconcile once
    // flags are live — gating on `flagsInitialized` keeps the transient
    // all-false while LaunchDarkly loads from bouncing a legitimate tab.
    useEffect(() => {
      if (!flagsInitialized) return;
      setActiveTab(current => resolveVisibleClipTab(current, tabVisibility));
    }, [flagsInitialized, tabVisibility]);

    // PLAT-10948: keep the clip-preview view state in the URL. Consolidated into
    // a single write because react-router (6.30) rebuilds `setSearchParams` each
    // render and hands its updater the render-closure `searchParams`, not the
    // live URL — so two per-param writes in the same tick both diff against the
    // same stale base and the second drops the first. Pushes a history entry
    // only when `?clipId` is first added (list -> preview) so Back returns to the
    // list; every other change replaces.
    const selectedLanguage = sessionStore.selectedLanguage;
    // The AR variant id when it has one, else the parent clip id — some variants
    // (e.g. an unrendered format) have no persisted id. A null value leaves
    // `?clipId` untouched rather than deleting it, since an absent param is
    // how SocialClipsView detects a Back-close.
    const clipUrlId = activeVideoId ?? clip.id ?? null;
    const clipSafeZone = serializeSafeZone(sessionStore.selectedSafeZone);
    const pinnedPublishRecordId = sessionStore.pinnedPublishRecordId;
    useEffect(() => {
      const { params, changed, addingClip } = computeClipPreviewSearch(
        searchParams,
        {
          clipId: clipUrlId,
          clipTab: activeTab,
          clipLang: selectedLanguage,
          clipPlayback: previewMode,
          clipSafeZone,
          clipPublishRecord: pinnedPublishRecordId,
        },
      );
      if (!changed) return;
      setSearchParams(params, { replace: !addingClip });
    }, [
      clipUrlId,
      activeTab,
      selectedLanguage,
      previewMode,
      clipSafeZone,
      pinnedPublishRecordId,
      searchParams,
      setSearchParams,
    ]);

    // PLAT-11096 — where the story's topics are spoken, for the Add Clips
    // picker's topic filter. Resolved here rather than in the panel: it needs
    // the story's transcript and the topic mapping, both of which live on
    // stores this modal already holds. Only while the Reel Editor is the open
    // tab — the picker is a click away from there, and no other tab asks.
    const topicSpans = useStoryTopicSpans({
      story,
      enabled: enableBasicReelCreation && activeTab === 'reelEditor',
    });
    // PLAT-11109 — the story's own transcript, for the scrub readout of a clip
    // previewed in the Add Clips picker. A candidate has no timeline of its
    // own, so its words are sliced out of this by source range. Read here for
    // the same reason `topicSpans` is: this modal holds the stores, and the
    // panel takes its story data as props.
    const sourceElements = useTranscription().originalTranscription?.elements;
    const imageRef = useRef<HTMLImageElement>(null);
    const videoPlayerRef = useRef<HTMLDivElement | null>(null);
    // Mirror the ref into state so consumers (e.g. SafeZoneOverlay) re-run
    // their layout effects once the element mounts.
    const [videoPlayerElement, setVideoPlayerElement] =
      useState<HTMLDivElement | null>(null);
    const setVideoPlayerRef = useCallback((el: HTMLDivElement | null) => {
      videoPlayerRef.current = el;
      setVideoPlayerElement(el);
    }, []);

    // PLAT-11288 — the in-progress drawing for this clip. Component state, not
    // the comment store: an unposted drawing is nobody else's business and is
    // discarded when the clip closes. Ink starts from the author's own comment
    // colour so their marks match their markers.
    const annotations = useClipAnnotations(
      personColors.get(userIdentity.currentEditor) ?? DEFAULT_INK,
    );
    const isAnnotating = annotations.tool !== null;
    // PLAT-11291 — the saved drawing currently painted on the frame, and the
    // control-bar switch that hides all of them.
    const [shownDrawing, setShownDrawing] = useState<Comment | null>(null);

    const [isPseudoFullscreen, setIsPseudoFullscreen] = useState(false);
    const toggleFullscreen = useCallback(
      () => setIsPseudoFullscreen(v => !v),
      [],
    );
    useEffect(() => {
      if (!isPseudoFullscreen) return;
      const handleKey = (e: KeyboardEvent) => {
        if (e.key !== 'Escape') return;
        // Stop the modal's window-level Escape listener from also firing and
        // closing the share modal — Escape should only exit fullscreen.
        e.stopPropagation();
        e.stopImmediatePropagation();
        setIsPseudoFullscreen(false);
      };
      document.addEventListener('keydown', handleKey, true);
      return () => document.removeEventListener('keydown', handleKey, true);
    }, [isPseudoFullscreen]);
    const [openReframeModal, setOpenReframeModal] = useState(false);
    const [publishedReady, setPublishedReady] = useState(false);
    const [publishingDimension, setPublishingDimension] = useState<
      string | null
    >(null);
    const [publishStartTime, setPublishStartTime] = useState<Date | null>(null);
    const [publishingVideoId, setPublishingVideoId] = useState<string | null>(
      null,
    );

    const mainClip = sessionStore.mainClip;
    const creatingDimension = sessionStore.creatingDimension;

    // Export overflow menu (anchored to the Export button, top-right).
    const exportButtonRef = useRef<HTMLButtonElement>(null);
    const [exportOpen, setExportOpen] = useState(false);

    // Load clip resources (videoSource, transcription, karaoke, scenes) when
    // the clip doesn't yet have them. Covers all entry points.
    useClipResourceLoader(clip);

    // Bridge render-status updates from VideoCreatorStore into the session
    // store's deep-cloned variants (listens for VIDEO_RENDER_FINISHED_EVENT).
    useRenderStatusObserver(sessionStore);

    const clips = [mainClip, ...(mainClip.associatedVideos || [])];

    const activeClip = sessionStore.activeVariant;

    // ShareModalPostsContent fans this clip out to every ClipPostItem, whose
    // "already shared" state reads `_allReferencingSharedContents`. Spreading
    // unconditionally is safe here: variants live in an `observable.ref` Map as
    // plain deep clones, so there is no observability to sever.
    const activeClipForPosts = useMemo(
      () => ({
        ...activeClip,
        _allReferencingSharedContents: resolveClipSharedContents(
          activeClip,
          story?.otherVideos,
        ),
      }),
      [activeClip, story?.otherVideos],
    );

    // The Downloads tab + Publish & Share panel read prior renders through
    // PublishRecordsStore (a session cache keyed by videoId). Scope that cache
    // to this modal: on close, clear every variant's entry so the next open
    // re-fetches fresh. A ref holds the latest variant-id set (refreshed each
    // render) so a variant created mid-session is cleared too.
    const publishRecordsStore = usePublishRecordsStore();
    const variantIdsRef = useRef<string[]>([]);
    variantIdsRef.current = clips
      .map(c => c.id)
      .filter((id): id is string => !!id);
    useEffect(
      () => () => publishRecordsStore.clear(...variantIdsRef.current),
      [publishRecordsStore],
    );

    const sharedRendererProps = useClipPreviewRenderer({
      story,
      mode: 'interactive',
      autoPlay: false, // Don't autoplay - let user control
      sessionStore,
      // Pass the prop straight through — used only for dep-array change
      // detection inside the hook so the renderer's init effect re-fires
      // in the same render where `videoSource` arrives.
      clipPropForReadiness: clip,
    });

    // PLAT-12102 — the contextual toolbars over the live canvas, on the same
    // gate as the graphics-card chip (DS8). They read the clip through their
    // own host (DS1) and a clip-scoped store context (DS3): the canvas sits
    // outside the transcript pane's provider, so without it the captions bar
    // would read the Creator Studio caption store.
    const toolbarsMounted =
      enableCompositionCanvasQuickEdit &&
      !isAnnotating &&
      previewMode === 'live';
    // The text bar's Edit opens the chip layer's quick-edit popover for a
    // text's content: the layer fills this, the host hands it to the bar.
    const quickEditHandleRef = useRef<CompositionCanvasQuickEditHandle | null>(
      null,
    );
    const toolbarHost = useClipPreviewToolbarHost({
      adapter: sharedRendererProps.adapter ?? undefined,
      editorStore: sharedRendererProps.editorStore ?? undefined,
      videoSourceStore: sharedRendererProps.videoSourceStore ?? undefined,
      rendererContainerRef: sharedRendererProps.rendererContainerRef,
      quickEdit: quickEditHandleRef,
    });
    const realRootStore = useStore();
    const toolbarStoreScope = useMemo(
      () =>
        sharedRendererProps.editorStore && sharedRendererProps.videoSourceStore
          ? createClipRootProxy(
              realRootStore,
              sharedRendererProps.editorStore,
              sharedRendererProps.videoSourceStore,
            )
          : null,
      [
        realRootStore,
        sharedRendererProps.editorStore,
        sharedRendererProps.videoSourceStore,
      ],
    );

    // PLAT-12102 DS7 — undo and redo the clip's edits from the keyboard while
    // its live preview is up, as Creator Studio's global scope does. Not while
    // typing, not under a dialog opened over the preview, and not on the Reel
    // Editor tab, which binds its own. The transcript handles its own Cmd+Z
    // and stops it before it gets here.
    const clipUndoScope = useMemo<ScopeRegistration>(() => {
      const adapter = sharedRendererProps.adapter;
      const guard = () => !isEditableElementFocused() && !isModalDialogOpen();
      return {
        scopeId: 'clipPreview',
        isActive: () => !!adapter,
        shortcuts: [
          {
            id: 'clipPreview.undo',
            chord: [
              { code: 'KeyZ', meta: true },
              { code: 'KeyZ', ctrl: true },
            ],
            guard,
            run: () => {
              adapter?.undo();
            },
          },
          {
            id: 'clipPreview.redo',
            chord: [
              { code: 'KeyY', meta: true },
              { code: 'KeyY', ctrl: true },
              { code: 'KeyZ', meta: true, shift: true },
              { code: 'KeyZ', ctrl: true, shift: true },
            ],
            guard,
            run: () => {
              adapter?.redo();
            },
          },
        ],
      };
    }, [sharedRendererProps.adapter]);
    useShortcutScope(clipUndoScope, {
      enabled: toolbarsMounted && activeTab !== 'reelEditor',
    });

    // PLAT-11445 — audio-first clip: veil the black canvas while the
    // composition sources the editor audio, play the session's local upload
    // when this tab still holds it, hand off to the rendition when it lands.
    const audioFirst = useClipAudioFirstCanvas({
      adapter: sharedRendererProps.adapter,
      renderer: sharedRendererProps.renderer,
      story,
      isLoading: sharedRendererProps.isLoading,
      setIsLoading: sharedRendererProps.setIsLoading,
      setCanvasHidden: sharedRendererProps.setCanvasHidden,
    });

    // Same gate as Creator Studio's Publish and the export menu's Publish item:
    // while the renditions are still processing the composition is audio-first
    // and publishing it would render audio over black.
    const publishGate = useEnhancementPublishGate();
    useEffect(() => {
      if (publishGate.isBlocked) setExportOpen(false);
    }, [publishGate.isBlocked]);
    // PLAT-12077 — opening a Clip Producer clip builds its source in the
    // background; until that lands there is nothing to render.
    const isAwaitingExpansion = isClipAwaitingExpansion(mainClip);
    // PLAT-11508 follow-up — size the frame from the composition the renderer
    // actually holds, so the video fills its canvas instead of sitting
    // letterboxed inside a box built from a `selectedDimension` that drifted
    // (reproducible after a Creator Studio round trip). The rendered/published
    // preview is a real mp4 at the selected dimension, so it keeps using that.
    const liveAspectRatio = useRenderedAspectRatio(
      sharedRendererProps.videoSourceStore,
      selectedDimension,
    );

    const {
      playbackState,
      onTimeChange: onLiveTimeChange,
      onPlay: onLivePlay,
      onPause: onLivePause,
      onDurationChange: onLiveDurationChange,
      setSeekTime,
      setIsSeeking,
    } = usePlaybackState({ autoPlay: false });

    useRendererPlayback({
      renderer: sharedRendererProps.renderer,
      editorStore: sharedRendererProps.editorStore,
      isReady: sharedRendererProps.isReady,
      callbacks: {
        onTimeChange: onLiveTimeChange,
        onPlay: onLivePlay,
        onPause: onLivePause,
        onDurationChange: onLiveDurationChange,
      },
    });

    // PLAT-11063 — the Reel Editor being opened, counted out here rather than in
    // the panel, which stays mounted for the life of the modal. The hook holds
    // the reasoning for both halves of that.
    useTrackReelEditorOpened({
      open: activeTab === 'reelEditor',
      rendererReady: sharedRendererProps.isReady,
      storyId: story?.id,
      clipId: clip.id,
      census: () => {
        const timeline = sharedRendererProps.editorStore?.editor.timeline;
        if (!timeline) return undefined;
        const reel = (clipJson as ClipMetadata | undefined)?.reel;
        return {
          section_count: deriveReelSections(timeline).length,
          chapter_count: reel?.chapters.length ?? 0,
          disabled_section_count: reel?.disabledSections?.length ?? 0,
        };
      },
    });

    // PLAT-11213 — the reel's own name. Not `videoCreator.renameVideo`: it
    // throws without a `currentVideo`, which Content Studio has none of, and it
    // saves the current and parent videos as a side effect (DS10).
    const renameClipService = useMemo(
      () =>
        datoClientStore
          ? new RenameClipService({ datoClientStore })
          : undefined,
      [datoClientStore],
    );
    /**
     * Where the new title lands in memory, mirroring what `renameVideo` does for
     * every other rename path: the clip list reads `story.otherVideos`, and the
     * preview cache spreads its own copy of the clip over the one it is handed
     * (`enrichClipFromCache`), so a stale title there would come back on reopen.
     */
    const applyReelName = useCallback(
      (title: string) => {
        if (!clip.id) return;
        runInAction(() => {
          const currentStory = videoCreator.story;
          if (!currentStory) return;
          currentStory.otherVideos = (currentStory.otherVideos ?? []).map(v =>
            v.id === clip.id ? { ...v, title } : v,
          );
        });
        clipPreviewStore.invalidate(clip.id);
      },
      [clip.id, videoCreator, clipPreviewStore],
    );
    /**
     * DS9 — generating reads Sections, so it is offered only where there are
     * Sections: a reel, with the flag on. The rename is not reel-specific and is
     * offered for every clip the preview opens.
     */
    const isReelClip = enableBasicReelCreation && !!clipJson?.isReel;

    // PLAT-11249 — the explicit Clip-to-Reel action behind the Reel Editor
    // tab's empty state. The hook saves the reel, opens it (which swaps this
    // modal onto the new record, landing on its Reel Editor tab) and says so
    // in the top toast.
    const { createReelFromClip, isSaving: isSavingReelFromClip } =
      useSaveClipsAsReel();

    const reelName = useReelAutoName({
      clipId: clip.id,
      clipTitle: clip.title ?? '',
      storyId: story?.id,
      storyTitle: story?.title,
      showcaseId: story?.primaryShowcase?.id,
      timeline: sharedRendererProps.editorStore?.editor.timeline,
      reel: sharedRendererProps.adapter?.currentVideo?.clipJson?.reel,
      enabled: isReelClip,
      datoClientStore,
      renameClipService,
      onRenamed: applyReelName,
    });
    // Master volume/mute for both preview modes. One state instance so the
    // setting carries over when switching Edit Mode ↔ Published Version.
    // Renderer pushes only while Edit Mode is the visible, loaded player;
    // Published mode is driven by the Mux players' volume/muted props.
    const volumeControls = usePlayerVolume({
      enabled:
        previewMode === 'live' &&
        sharedRendererProps.isReady &&
        !sharedRendererProps.isLoading,
      getRenderer: () => sharedRendererProps.rendererRef.current,
      getActiveElementIds: () =>
        sharedRendererProps.adapter?.activeElementIds ?? [],
      isPlaying: playbackState.isPlaying,
    });

    const {
      playbackState: publishedPlaybackState,
      onTimeChange: onPublishedTimeChange,
      onPlay: onPublishedPlay,
      onPause: onPublishedPause,
      onDurationChange: onPublishedDurationChange,
      setSeekTime: setPublishedSeekTime,
      setIsSeeking: setPublishedIsSeeking,
    } = usePlaybackState();

    // PLAT-11061 — "Edit Transcript" on a Section: switch to the Transcript tab,
    // then ask the transcript mounted there to show that passage.
    //
    // Two steps rather than one because both panes are always mounted and the
    // hidden one is `display: none` — it has no layout, so a jump dispatched in
    // the same breath as the tab switch would measure a zero-height container
    // and scroll nowhere. The target is parked in state and consumed once the
    // flip has committed.
    const [pendingTranscriptTarget, setPendingTranscriptTarget] = useState<{
      index: number;
      tokenId?: string;
    } | null>(null);
    const transcriptAdapter = sharedRendererProps.adapter;
    // `useLayoutEffect`, not `useEffect` + rAF: the container's box exists as
    // soon as this commit lands, and `getBoundingClientRect` inside the jump
    // forces the layout it needs — so the scroll happens in the same paint as
    // the tab switch rather than one frame after it, which is what a
    // jump-then-settle flicker is (CLAUDE.md pitfall 15).
    useLayoutEffect(() => {
      if (!pendingTranscriptTarget || activeTab !== 'transcript') return;
      // Cleared whether or not a transcript is listening: a target left in
      // state re-fires on the next visit to the tab, which reads as the
      // transcript jumping on its own.
      setPendingTranscriptTarget(null);
      // Always scoped, never broadcast: an unscoped jump would also move the
      // story-level transcript mounted behind the modal. No adapter, no
      // transcript of ours to move either.
      if (!transcriptAdapter) return;
      dispatchTranscriptIndexJump(pendingTranscriptTarget, transcriptAdapter);
    }, [pendingTranscriptTarget, activeTab, transcriptAdapter]);

    // PLAT-11877 — the transcript's globe opens the Languages tab; picking a
    // language there (or its back arrow) returns to the transcript to read it.
    const openLanguagesTab = useCallback(() => setActiveTab('languages'), []);
    const openTranscriptTab = useCallback(() => setActiveTab('transcript'), []);

    const editSectionTranscript = useCallback(
      (target: { index: number; tokenId?: string }) => {
        setActiveTab('transcript');
        setPendingTranscriptTarget(target);
      },
      [],
    );

    // PLAT-11163 — the reverse trip: a transcript Section heading opens the
    // Reel Editor at its row. Same two-step as the forward jump above, in the
    // other direction: park the target, switch the tab, and let the panel
    // consume it once its pane has layout. Navigation on both sides — nothing
    // about the reel is written by travelling either way.
    const [pendingReelRevealSectionId, setPendingReelRevealSectionId] =
      useState<string | null>(null);
    const openSectionInReelEditor = useCallback((sectionId: string) => {
      setActiveTab('reelEditor');
      setPendingReelRevealSectionId(sectionId);
    }, []);
    const handleReelRevealHandled = useCallback(
      () => setPendingReelRevealSectionId(null),
      [],
    );

    const adapterIsSaving = sharedRendererProps?.adapter?.isSaving ?? false;
    useEffect(() => {
      if (savingRef) savingRef.current = adapterIsSaving;
      onSavingChange?.(adapterIsSaving);
    }, [adapterIsSaving, savingRef, onSavingChange]);

    const clipAdapter = sharedRendererProps?.adapter;
    useEffect(() => {
      if (!beforeCloseRef) return undefined;
      beforeCloseRef.current = clipAdapter
        ? async () => {
            await clipAdapter.flushPendingSave();
            if (!clipAdapter.savingError) handOffPreviewClip(sessionStore);
          }
        : null;
      return () => {
        beforeCloseRef.current = null;
      };
    }, [clipAdapter, beforeCloseRef, handOffPreviewClip, sessionStore]);

    // PLAT-11911: Creator Studio loads what's stored, so leaving with an
    // unsaved edit would open the clip without it.
    const [isLeavingForCreator, setIsLeavingForCreator] = useState(false);
    const handleEditInCreator = async () => {
      setIsLeavingForCreator(true);
      try {
        await clipAdapter?.flushPendingSave();
      } catch {
        // The autosave chip shows the failed save; stay in the preview.
        setIsLeavingForCreator(false);
        return;
      }
      navigate(sessionStore.activeVariant, {
        language: sessionStore.selectedLanguage,
        session: clipAdapter?.savingError ? undefined : sessionStore,
      });
    };

    // PLAT-11654 — every edit in this modal is auto-saved by the adapter, so
    // the title row shows the same cloud icon + "Saving…/Saved" chip as
    // Articles instead of a one-state "Saving..." badge.
    const autosave = useClipAutosaveStatus(sharedRendererProps?.adapter);

    const [clipImage, setClipImage] = useState<string>(
      getClipThumbnail(activeClip),
    );
    useEffect(() => {
      setClipImage(getClipThumbnail(activeClip));
    }, [activeClip]);

    // Track clip edit started when modal opens
    useEffect(() => {
      EventTracker.trackClipEditStarted({
        clip_id: clip.id,
        story_id: story?.id,
      });
    }, []);

    // PLAT-11042: time from pop-up open until the clip preview is usable.
    // Keyed by clip.id — the modal can swap clips without unmounting, and
    // each clip gets its own timer (startTimer also supersede-cancels any
    // prior one). Closing before ready emits a cancelled action with
    // elapsed_ms (a completed timer makes this cleanup a no-op).
    useEffect(() => {
      EventTracker.startTimer('clip_editor_usable_popup', {
        surface: 'content_popup',
        videoId: clip.id,
        clipTitle: clip.title,
        storyId: story?.id,
        storyTitle: story?.title,
      });
      // Never-usable beacon: if the pop-up still isn't usable after 60s,
      // say so explicitly (no-op if the timer already ended).
      const timeoutBeacon = setTimeout(() => {
        EventTracker.reportTimerStillRunning('clip_editor_usable_popup', {
          videoId: clip.id,
        });
      }, 60_000);
      return () => {
        clearTimeout(timeoutBeacon);
        EventTracker.cancelTimer('clip_editor_usable_popup', 'popup_closed');
      };
    }, [clip.id]);

    // The published player's readiness belongs to one specific video: reset
    // it when the clip or the selected variant changes so a stale true can't
    // end the new clip's timer early.
    useEffect(() => {
      setPublishedReady(false);
    }, [clip.id, selectedDimension]);

    // PLAT-11042: pop-up "usable" = video player ready AND transcript ready
    // (either preview path counts as video-ready). Per-part marks let
    // downstream alerting use video-only, transcript-only, or both. When the
    // transcript tab is feature-flagged off, the transcript gate is bypassed
    // (it would otherwise never pass and false-fire the timeout beacon).
    const usableTranscriptionStore = useTranscription();
    const popupVideoReady = publishedReady || sharedRendererProps.isReady;
    const popupTranscriptReady =
      !contentStudioModalEnableTranscript ||
      usableTranscriptionStore.isTranscriptionReady;
    useEffect(() => {
      if (popupVideoReady) {
        EventTracker.markTimer('clip_editor_usable_popup', 'video_ready_ms');
      }
    }, [popupVideoReady]);
    useEffect(() => {
      if (popupTranscriptReady) {
        EventTracker.markTimer(
          'clip_editor_usable_popup',
          'transcript_ready_ms',
        );
      }
    }, [popupTranscriptReady]);
    useEffect(() => {
      if (popupVideoReady && popupTranscriptReady) {
        EventTracker.endTimer('clip_editor_usable_popup');
      }
    }, [popupVideoReady, popupTranscriptReady]);

    useEffect(() => {
      if (previewMode !== 'live' && sharedRendererProps.isReady) {
        sharedRendererProps.pause();
      }
    }, [previewMode, sharedRendererProps.isReady]);

    // Persist the selected dimension so reopening the modal returns to the
    // last-used aspect ratio instead of defaulting to 16:9.
    useEffect(() => {
      if (clip.id) {
        videoCreator.lastClipDimension.set(clip.id, selectedDimension);
      }
    }, [selectedDimension]);

    // Mirror safe-zone selection + variant flags back to the singleton
    // `ClipPreviewStore` on every change so they survive modal close/reopen.
    // Read at the next open by `useClipEditSession` to seed the new session.
    // Global scope (not keyed per clip) — matches the QA cleanup direction.
    useEffect(() => {
      clipPreviewStore.setLastSafeZone(
        sessionStore.selectedSafeZone,
        sessionStore.safeZoneVariantsSnapshot,
      );
    }, [sessionStore.selectedSafeZone, sessionStore.safeZoneVariantsSnapshot]);

    // PLAT-11748 — remember the transcript language per clip, the same way, so
    // reopening the clip resumes in the language the user was working in
    // instead of dropping back to Original.
    useEffect(() => {
      if (clip.id) {
        clipPreviewStore.setLastClipLanguage(clip.id, selectedLanguage);
      }
    }, [clip.id, selectedLanguage]);

    const playerRef = useRef<MuxPlayerElement | null>(null);

    /**
     * This clip's variants' publish records. The dropdown's DRAFT / final
     * badges and the header's publish line both used to read the legacy
     * `videoFilePrimary` / `lastPublishJson` fields, which a clip published only
     * in a translation never writes — so a dubbed publish showed DRAFT on the
     * very aspect ratio it had just been published in (PLAT-11591).
     *
     * Story-scoped and incremental, so opening this modal over a loaded clips
     * grid costs no request; the grid has already fetched these records.
     */
    const publishRecordsFor = useStoryClipPublishRecords(
      videoCreator.story?.id,
      [mainClip],
    );
    const clipPublishState = deriveClipPublishState(
      mainClip,
      publishRecordsFor,
      videoCreator.story?.originalVideoLanguage || 'en',
    );

    // Build dimensions list with ALL aspect ratios, marking non-existing ones as new.
    // Must use mainClip (from session store) not the clip prop — the session store
    // has newly-added variants in its associatedVideos that the prop doesn't.
    const existingFormats = clipPublishState.formats;
    const allFormats: DimensionItem[] = useMemo(() => {
      const existingArs = new Set(existingFormats.map(f => f.aspectRatio));
      const existing: DimensionItem[] = existingFormats.map(f => ({
        aspectRatio: f.aspectRatio,
        isPublished: f.isPublished,
        isNew: false,
        isCreating: creatingDimension === f.aspectRatio,
      }));
      const missing: DimensionItem[] = Object.values(AspectRatio)
        .filter(ar => !existingArs.has(ar))
        .map(ar => ({
          aspectRatio: ar,
          isPublished: false,
          isNew: true,
          isCreating: creatingDimension === ar,
        }));
      const all = [...existing, ...missing];
      // Sort by canonical enum order: 16:9, 1:1, 9:16
      const canonicalOrder = Object.values(AspectRatio);
      all.sort(
        (a, b) =>
          canonicalOrder.indexOf(a.aspectRatio as AspectRatio) -
          canonicalOrder.indexOf(b.aspectRatio as AspectRatio),
      );
      return all;
    }, [existingFormats, creatingDimension]);

    const posterUrl = getPosterUrl(
      clipImage,
      activeClip?.videoFilePrimary?.width!,
      activeClip?.videoFilePrimary?.height!,
    );

    // Every publish this clip has, in either place it can be recorded — so the
    // header line describes a translation-only publish rather than falling back
    // to the "publish your clips" empty message on an already-published clip.
    const allPublishInfo = clipPublishState.publishInfo;
    const allPublishLanguage = clipPublishState.latest?.language ?? null;

    const saveToDato = async (
      videoIdx: number,
      imageUrl: string,
      selectedClip = activeClip,
      targetImage = selectedImage,
    ) => {
      try {
        if (!imageUrl || !targetImage) return;
        const isInDato = imageUrl.startsWith('https://www.datocms-assets.com');
        let url = imageUrl;
        let id = targetImage.id;

        if (!isInDato) {
          if (targetImage.type === 'stock') setSavingStock(true);
          const fileName = getRandomFileName(searchText!) || uuid();

          const newPhotoData: FileData & { fileName: string } = {
            type: targetImage.type,
            url,
            fileName,
            alt: searchText,
            title: searchText,
          };
          const newUpload =
            await datoClientStore.assetRepository?.uploadFile(newPhotoData);
          if (newUpload) {
            url = newUpload.url;
            id = newUpload.id;
            if (targetImage.type !== 'quotes') {
              const assets = [
                ...(videoCreator.story?.storyAssets || []),
                {
                  id,
                  title: searchText,
                  customData: {},
                  format: newUpload.format,
                  mimeType: newUpload.mime_type,
                  url,
                  responsiveImage: {
                    srcSet: url,
                    alt: searchText,
                    title: searchText,
                  },
                  video: null,
                  _createdAt: new Date(),
                },
              ] as Artifact[];
              videoCreator.story!.storyAssets! = assets;
              await videoCreator.updateStory(videoCreator.story!);
            }

            setClipImage(url);

            updateOtherVideos(
              videoIdx,
              url,
              targetImage.shareableImageId,
              selectedClip,
            );

            setSavingStock(false);
            setSavingQuote(false);
            setSelectedImage(null);
          } else {
            return;
          }
        }

        if (id && url) {
          const shareableImageId = targetImage.shareableImageId;
          // Update the session store's variant with the new thumbnail
          const targetAr = selectedClip.aspectRatio || selectedDimension;
          sessionStore.updateThumbnail(
            targetAr,
            { url },
            targetImage.shareableImageId || '',
          );

          await datoClientStore.videoRepository?.updateThumbnail(
            selectedClip.id || clip.id!,
            id,
            shareableImageId,
          );
          setSelectedImage(null);
        }
        // eslint-disable-next-line no-empty
      } catch (error) {}
    };

    const updateOtherVideos = (
      videoIdx: number,
      url: string,
      shareableImageId: string | undefined,
      selectedClip = activeClip,
    ) => {
      if (videoIdx > -1) {
        if (selectedClip.id === clip.id) {
          videoCreator!.story!.otherVideos[videoIdx].thumbnail = { url };
          if (shareableImageId) {
            videoCreator!.story!.otherVideos[videoIdx].shareableImageId =
              shareableImageId || '';
          }
          if (story?.finalVideo?.id === clip.id) {
            const video = story!.finalVideo! as Video;

            (story!.finalVideo! as Video) = {
              ...video,
              thumbnail: {
                ...(video.thumbnail || {}),
                url,
              },
            };
          }
        } else {
          const associatedVideoIdx = videoCreator!.story!.otherVideos[
            videoIdx
          ].associatedVideos.findIndex(v => v.id === selectedClip.id);
          videoCreator!.story!.otherVideos[videoIdx].associatedVideos[
            associatedVideoIdx
          ].thumbnail = { url };

          if (shareableImageId) {
            videoCreator!.story!.otherVideos[videoIdx].associatedVideos[
              associatedVideoIdx
            ].shareableImageId = shareableImageId || '';
          }
        }
      }
    };

    const onReplaceAction = async () => {
      if (!selectedImage) return;
      let url = selectedImage.url;

      if (selectedImage.type === 'quotes') {
        url = await createImageElement(
          imageRef.current!,
          url,
          selectedImage.quote!,
          () => {
            setSavingQuote(true);
            togglePhotosModal(false);
          },
          activeClip.aspectRatio || videoCreator.getDefaultAspectRatio(),
        );
      }

      setClipImage(selectedImage.url);

      const videoIdx = videoCreator?.story?.otherVideos.findIndex(
        video => video.id === clip.id,
      );

      if (videoIdx === undefined) {
        togglePhotosModal(false);
        return;
      }

      updateOtherVideos(
        videoIdx,
        selectedImage.url,
        selectedImage.shareableImageId,
      );

      togglePhotosModal(false);
      await saveToDato(videoIdx, url);
    };

    const onSelectSafeZone = (selection: SafeZoneSelection) => {
      if (selection === sessionStore.selectedSafeZone) return;
      sessionStore.selectSafeZone(selection);
      const { channel, format } = splitSafeZoneSelection(selection);
      EventTracker.trackSafeZoneSelected({
        selection,
        channel,
        format,
        aspect_ratio: selectedDimension,
        clip_id: clip.id,
        story_id: story?.id,
      });
    };

    const onToggleSafeZoneVariant = (key: SafeZoneVariantKey) => {
      const selection = sessionStore.selectedSafeZone;
      if (selection === 'none') return;
      sessionStore.toggleSafeZoneVariant(key);
      EventTracker.trackSafeZoneVariantToggled({
        selection,
        variant: key,
        enabled: sessionStore.safeZoneVariant(key),
        aspect_ratio: selectedDimension,
        clip_id: clip.id,
        story_id: story?.id,
      });
    };

    const onSelectDimension = async (dimension: string) => {
      if (dimension === selectedDimension) {
        return;
      }

      const existingClip = clips.find(v => v.aspectRatio === dimension);
      const isNewVariant = !existingClip;

      const previousDimension = selectedDimension;
      if (isNewVariant) {
        sessionStore.setCreatingDimension(dimension);
      }
      sharedRendererProps.setIsLoading(true);

      try {
        const arStart = performance.now();
        await sharedRendererProps.adapter!.loadVideoWithAspectRatio(
          dimension as AspectRatio,
        );
        const arEnd = performance.now();
        logger.debug(
          `performance: loadVideoWithAspectRatio for ${dimension} took ${arEnd - arStart} ms`,
        );

        // Determine preview mode for the target variant
        const selectedClip =
          existingClip ?? clips.find(v => v.aspectRatio === dimension);
        if (
          isNewVariant ||
          !selectedClip?.videoFilePrimary?.video?.muxPlaybackId
        ) {
          sessionStore.setPreviewMode('live');
        }
      } catch (error) {
        logger.error('Failed to switch aspect ratio:', error);
        sessionStore.selectDimension(previousDimension);
      } finally {
        if (isNewVariant) {
          sessionStore.setCreatingDimension(null);
        }
      }

      // Shareable image handling
      const selectedClip = clips.find(v => v.aspectRatio === dimension);

      const shareableImageId = clips.find(
        v => v.shareableImageId,
      )?.shareableImageId;

      if (!shareableImageId) return;

      if (selectedClip?.thumbnail) {
        setClipImage(selectedClip.thumbnail.url);
      }

      if (selectedClip && !selectedClip.shareableImageId && selectedClip.id) {
        const shareableImage = videoCreator.story?.shareableImages?.find(
          s => s.id === shareableImageId,
        );

        if (shareableImage) {
          const imageFile = shareableImage.imagefile;
          const url = imageFile?.url || '';
          const quote = shareableImage.quote;
          const blobUrl = await createImageElement(
            imageRef.current!,
            url,
            quote,
            () => {
              setSavingQuote(true);
            },
            selectedClip.aspectRatio,
          );

          const videoIdx = videoCreator?.story?.otherVideos.findIndex(
            video => video.id === clip.id,
          );

          if (videoIdx !== undefined && videoIdx !== -1) {
            const data = {
              type: 'quotes' as 'quotes',
              url: imageFile?.url || '',
              alt: imageFile?.title || '',
              id: imageFile?.id || '',
              width: imageFile?.width || '',
              height: imageFile?.height || '',
              quote: shareableImage.quote,
              shareableImageId: shareableImage.id,
            };

            await saveToDato(videoIdx, blobUrl, selectedClip, data);
          }
          setSavingQuote(false);
        }
      }
    };

    const clipElement = (
      (activeClip as any)?.videoJson || activeClip?.videoSource
    )?.elements?.find((el: any) => videoCreator.isOriginalVideoElement(el));
    const reelCuts = clipJson?.isReel
      ? ((activeClip as any).videoJson || activeClip.videoSource)?.elements
          .filter((el: any) => videoCreator.isOriginalVideoElement(el))
          .map((el: any) => {
            const cut: Cut = {
              source: el.source,
              trim_start: el.trim_start ?? 0,
              duration: el.duration ?? 0,
              id: el.id,
            };
            return cut;
          })
      : undefined;

    function updateTimeOnPublishedVideo(
      event: MuxPlayerElementEventMap['timeupdate'],
    ) {
      const startTime = 0;
      // The render's own measured length first: for a translated render there
      // is no `videoFilePrimary`, and the clip's edited duration is only an
      // estimate of what came out of the renderer.
      const endTime =
        publishedSource?.durationSeconds ||
        activeClip?.videoFilePrimary?.video?.duration ||
        clipJson?.duration;

      const videoElement = event.target as HTMLVideoElement;
      const seconds = videoElement.currentTime;
      onPublishedTimeChange(seconds);
      if (seconds > (endTime || 0)) {
        setTimeout(() => {
          playerRef.current!.currentTime = startTime;
        }, 1000);
      }
    }

    const onPublishedLoadedMetadata = (
      event: MuxPlayerElementEventMap['loadedmetadata'],
    ) => {
      const video = event.target as HTMLVideoElement;
      // PLAT-11042: publishedReady feeds the popup usable timer (video gate).
      setPublishedReady(true);
      onPublishedDurationChange(video.duration);
      onPublishedTimeChange(video.currentTime);
      if (!video.paused) onPublishedPlay();
    };

    const playPublished = () => {
      void playerRef.current?.play();
    };

    const pausePublished = () => {
      playerRef.current?.pause();
    };

    const setPublishedTime = (time: number) => {
      if (playerRef.current) {
        playerRef.current.currentTime = time;
      }
    };

    const unifiedPlayback = useMemo<PlaybackBridge>(() => {
      if (previewMode === 'rendered') {
        return {
          isPlaying: publishedPlaybackState.isPlaying,
          currentTime: publishedPlaybackState.currentTime,
          duration: publishedPlaybackState.duration,
          isReady: publishedReady,
          seekTime: publishedPlaybackState.seekTime,
          setSeekTime: setPublishedSeekTime,
          isSeeking: publishedPlaybackState.isSeeking,
          setIsSeeking: setPublishedIsSeeking,
          play: playPublished,
          pause: pausePublished,
          setTime: setPublishedTime,
        };
      }
      return {
        isPlaying: playbackState.isPlaying,
        currentTime: playbackState.currentTime,
        duration: playbackState.duration,
        isReady: sharedRendererProps.isReady,
        seekTime: playbackState.seekTime,
        setSeekTime,
        isSeeking: playbackState.isSeeking,
        setIsSeeking,
        play: sharedRendererProps.play,
        pause: sharedRendererProps.pause,
        // Push the target time into playback state optimistically alongside the
        // renderer seek. On a newly created clip/variant the renderer's
        // `onTimeChange` chain to `usePlaybackState` can be severed (the base
        // handler in `useClipPreviewRenderer` gets re-set without
        // `useRendererPlayback` re-wrapping), so the video seeks but the
        // scrubber freezes. Driving `currentTime` here keeps the scrubber in
        // sync regardless; when the chain is intact the renderer echoes the same
        // time and the update is idempotent.
        setTime: (time: number) => {
          sharedRendererProps.setTime(time);
          onLiveTimeChange(time);
        },
      };
    }, [
      previewMode,
      playbackState,
      publishedPlaybackState,
      publishedReady,
      setPublishedSeekTime,
      setPublishedIsSeeking,
      playPublished,
      pausePublished,
      setPublishedTime,
      setSeekTime,
      setIsSeeking,
      sharedRendererProps.isReady,
      sharedRendererProps.play,
      sharedRendererProps.pause,
      sharedRendererProps.setTime,
      onLiveTimeChange,
    ]);
    /**
     * What the overlay paints. Composing wins over viewing: a reviewer with
     * marks in progress must never have them replaced by someone else's. A
     * saved drawing shows only while paused — ink over moving video is
     * meaningless, and painting every drawing during playback would make a
     * heavily reviewed clip unwatchable.
     */
    const visibleStrokes = useMemo(() => {
      // Hidden means hidden — checked before anything else. Previously this sat
      // below the composing branch, so any in-progress mark short-circuited it
      // and the toggle silently did nothing.
      if (!annotations.drawingsVisible) return [];
      if (isAnnotating || annotations.strokes.length > 0) {
        return annotations.strokes;
      }
      if (unifiedPlayback.isPlaying) return [];
      return shownDrawing?.drawing?.strokes ?? [];
    }, [
      annotations.drawingsVisible,
      isAnnotating,
      annotations.strokes,
      unifiedPlayback.isPlaying,
      shownDrawing,
    ]);

    /**
     * The strip in the control bar is gated twice: the feature's master flag,
     * and its own sub-flag. The reframe modal's copy answers a dead end a
     * creator can otherwise only close their way out of, while this one is a
     * new row under the player — worth being able to roll back on its own,
     * without taking the reframe modal's with it.
     */
    const showSceneStripInPlayer =
      contentStudioModalEnableScenesStrip &&
      contentStudioModalEnableScenesStripInPlayer;

    /**
     * Intro / end cards, which hold the screen outside every scene and would
     * otherwise leave the rail broken off with nothing to hover. `null` args
     * while the strip is hidden skip the rebuild entirely — the hook walks the
     * whole transcript on every timeline change.
     */
    const cardMarkers = useTimelineMarkers(
      showSceneStripInPlayer ? (sharedRendererProps.adapter ?? null) : null,
      showSceneStripInPlayer ? (sharedRendererProps.editorStore ?? null) : null,
    );
    const clipCards = useMemo(
      () => cardSpansFromMarkers(cardMarkers),
      [cardMarkers],
    );

    /**
     * Scenes of the clip as currently edited, for the read-only scenes strip
     * (PLAT-11236). Undefined when there is nothing to show — flag off, no editor
     * yet, or a clip with no scenes.
     *
     * `videoSourceStore.source` is the dep that makes this re-derive: the
     * timeline is not MobX-observable, but every refresh that can re-scene the
     * clip (an aspect ratio change above all) ends in `refreshTimeline`
     * replacing the observable source ref.
     */
    const sceneStrip = useMemo(() => {
      if (!showSceneStripInPlayer) return undefined;
      const timeline = sharedRendererProps.editorStore?.editor.timeline;
      if (!timeline || timeline.scenes.length === 0) return undefined;
      return {
        scenes: timeline.scenes,
        elements: timeline.allElements,
        cards: clipCards,
      };
    }, [
      showSceneStripInPlayer,
      sharedRendererProps.editorStore,
      sharedRendererProps.videoSourceStore?.source,
      sharedRendererProps.isReady,
      clipCards,
    ]);

    // What Published Version plays for this AR + language, and whether this AR
    // has a published render at all. Only the canonical (original-language)
    // cell mirrors to `videoFilePrimary`, so a clip published solely in a
    // translation needs its publish records to answer either question
    // (PLAT-11548).
    const {
      source: publishedSource,
      hasPublishedRender,
      displayLanguage: publishedSourceLanguage,
      sourceRecord: publishedSourceRecord,
    } = usePublishedClipSource({
      videoId: activeVideoId,
      adapter: sharedRendererProps.adapter,
      language: selectedLanguage,
      videoFilePrimary: activeClip.videoFilePrimary,
      originalLanguage: story?.originalVideoLanguage,
      pinnedRecordId: sessionStore.pinnedPublishRecordId,
    });

    /**
     * In Published Version, the header's publish line describes the render on
     * screen instead of the clip's most recent publish across variants — which
     * can be a different format and a different time than what is playing, and
     * now that a Downloads row can pin an older publish, a different publish
     * again (PLAT-11548). `null` = keep today's line.
     */
    const playingPublishInfo = useMemo<VideoLastPublish[] | null>(() => {
      if (previewMode !== 'rendered') return null;
      const record = publishedSourceRecord;
      if (!record?.publishedAt) return null;
      return [
        {
          timestamp: record.publishedAt,
          publisher: record.publishedBy ?? '',
          aspectRatio: record.aspectRatio,
          resolution: record.resolution,
        },
      ];
    }, [previewMode, publishedSourceRecord]);

    /**
     * The rest of the mode dropdown's label — which published version is
     * selected. A dropdown trigger states its current value, and now that a
     * Downloads row can pin an older publish, "Published Version" on its own
     * is not the value: the language and the publish time are. Rendered
     * quieter than the mode itself so the label reads as one thing with a
     * qualifier, not two competing labels (PLAT-11548).
     */
    const publishedVersionDetail = [
      publishedSourceLanguage
        ? formatLanguageCaption(publishedSourceLanguage)
        : null,
      publishedSourceRecord?.publishedAt
        ? formatDateTimeCompact(publishedSourceRecord.publishedAt)
        : null,
    ]
      .filter(Boolean)
      .join(' · ');

    // Whether the active clip has a rendered/playable version
    const hasRenderedVersion = useMemo(() => {
      return (
        hasPublishedRender ||
        !!activeClip.videoFilePrimary?.video?.muxPlaybackId
      );
    }, [activeClip, hasPublishedRender, videoCreator.renderingStatus]);

    // One-time mount guard: Published Version with no published dimension at
    // all falls back to live preview. Opening on a published dimension is the
    // session store's constructor's job (PLAT-12133) — switching dimensions
    // here, after mount, would leave the editor on the other one's timeline.
    const didInitialAutoSwitch = useRef(false);
    useEffect(() => {
      if (didInitialAutoSwitch.current) return;
      didInitialAutoSwitch.current = true;

      if (
        !hasRenderedVersion &&
        previewMode === 'rendered' &&
        !clips.some(c => !!c.videoFilePrimary)
      ) {
        sessionStore.setPreviewMode('live');
      }
    }, []);

    // The mirror of that guard, for the render the constructor couldn't see: a
    // translation-only publish leaves `videoFilePrimary` null, so the session
    // opened in Edit Mode with nothing to correct it. Records arrive after
    // mount, hence an effect rather than a constructor default. The store keeps
    // this to a single upgrade and refuses it outright once the mode has been
    // chosen — by `?clipPlayback=` or by the user.
    useEffect(() => {
      if (hasPublishedRender) sessionStore.adoptPublishedRender();
    }, [hasPublishedRender, sessionStore]);

    // A translated render is known from its publish record before its signed
    // URL comes back. Nothing may fall through to the story-level Mux fallback
    // in that window — it would play the whole story in place of the clip's
    // render for a beat.
    const publishedResolving = hasPublishedRender && !publishedSource;

    // For lightweight S3-hosted clips, fall back to clipJson.sourceUrl
    const clipSource =
      !hasRenderedVersion || publishedResolving
        ? null
        : publishedSource || clipJson?.muxPlaybackId
          ? null
          : clipElement?.source ||
            (clipJson as ClipMetadata | undefined)?.sourceUrl;

    const playbackId = useMemo(() => {
      if (!hasRenderedVersion || publishedResolving) return undefined;
      // The clip's own published render: a Mux id for the canonical one, and
      // nothing for a translated render, whose progressive MP4 rides
      // `renderedVideoSrc` below.
      if (publishedSource) return publishedSource.playbackId;
      if (clipJson?.muxPlaybackId) {
        return clipJson.muxPlaybackId;
      }
      // For S3-hosted clips, return undefined so the player uses clipSource instead
      if (clipElement?.source) return;
      if ((clipJson as ClipMetadata | undefined)?.sourceUrl) return;
      const finalVideo = story?.finalVideo?.videoFilePrimary?.video;
      const originalVideo = story?.originalVideo.video;
      return (finalVideo || originalVideo)?.muxPlaybackId;
    }, [
      publishedSource,
      publishedResolving,
      clipJson,
      clipElement,
      story,
      hasRenderedVersion,
    ]);

    /**
     * Mux asset backing the scrubber's hover thumbnails. Deliberately NOT
     * `playbackId` above: that falls back to the story's final/original video,
     * whose timeline is the full source. A clip is a transcript-driven cut of
     * that source, so source-time and clip-time only line up for a pure trim —
     * anywhere the clip drops words, a source frame would be the wrong moment.
     * Only the clip's own render is safe, and its absence (never published)
     * just means the hover card shows a timecode without a frame.
     */
    const hoverThumbnailPlaybackId =
      activeClip?.videoFilePrimary?.video?.muxPlaybackId;

    // Use clipImage directly as poster - posterUrl adds imgix params that break non-datocms URLs
    const effectivePosterUrl =
      clipImage ||
      posterUrl ||
      (playbackId
        ? `https://image.mux.com/${playbackId}/thumbnail.jpg`
        : undefined);

    // PLAT-10009: a PublishRecord-synthesized `videoFilePrimary` points at the
    // S3 render output and has no `muxPlaybackId`, so `playbackId` is empty.
    // MuxPlayer also accepts a plain `src` URL — fall back to the file URL so the
    // rendered preview plays (progressive MP4) until the render is Mux-processed.
    // PLAT-11548: a translated render arrives the same way, as a signed
    // CloudFront URL off its publish record.
    const renderedVideoSrc =
      !playbackId && publishedSource?.src ? publishedSource.src : undefined;

    // Clear the publishing indicator when the published video's ID is removed
    // from renderingVideoIds (i.e., handleVideoRenderingStatusUpdate has
    // processed the final status update for this video).
    useEffect(() => {
      if (
        publishingVideoId &&
        !videoCreator.renderingVideoIds.includes(publishingVideoId)
      ) {
        setPublishingDimension(null);
        setPublishStartTime(null);
        setPublishingVideoId(null);
      }
    }, [publishingVideoId, videoCreator.renderingVideoIds]);

    // The full set of aspect ratios with a render in flight. The backend
    // renders each AR concurrently (tracked in `renderingVideoIds`), so more
    // than one can be publishing at once — the dropdown badges them all.
    // Deriving from the store means the indicator survives the modal being
    // closed and reopened; the local `publishingDimension` is added on top to
    // cover the optimistic window before the render registers in the store.
    const publishingDimensions = new Set<string>(
      clips
        .filter(c => c?.id && videoCreator.renderingVideoIds.includes(c.id))
        .map(c => c.aspectRatio),
    );
    if (publishingDimension) publishingDimensions.add(publishingDimension);

    return (
      <>
        <Main data-testid="clip-share-modal">
          {videoCreator.showRefreshStoryForSocialProfile && (
            <SocialProfileRefreshOverlay />
          )}

          <LeftPane>
            <TitleContainer>
              <TitleRow>
                {/* PLAT-11246 — what is open, said outright: the same badge
                    the clip grid marks a reel's card with, so "this is a Reel"
                    reads the moment the modal opens rather than only back on
                    the card. A plain clip is labelled too — the badge is the
                    surface's identity, not a reel-only flourish. On the title
                    row, whose 30px the pill fits inside: on the hint line
                    below it grew the header and pulled the pane's divider out
                    of line with the right pane's. */}
                <ClipTypeBadge
                  $color={isReelClip ? 'purple' : 'blue'}
                  data-testid="clip-preview-type-badge"
                >
                  {isReelClip ? 'Reel' : 'Clip'}
                </ClipTypeBadge>
                {/* PLAT-11213 — the clip's title is the reel's name (DS1), and
                    the title row is where a creator already looks for it (DS7).
                    Editable for every clip, not only a reel: making the heading
                    editable is not a reel-specific change (DS9). */}
                <ClipPreviewTitle
                  name={reelName.name}
                  onRename={reelName.rename}
                  onGenerate={isReelClip ? reelName.generate : undefined}
                  isGenerating={reelName.isGenerating}
                  error={reelName.error}
                />
                <SaveStatusGroup>
                  <AutosaveStatusIcon
                    status={autosave.status}
                    lastSavedAt={autosave.lastSavedAt}
                    popoverZIndex={CLIP_PREVIEW_Z.modalPopover}
                  />
                  <AutosaveStatusIndicator
                    status={autosave.status}
                    lastSavedAt={autosave.lastSavedAt}
                  />
                </SaveStatusGroup>
                {!adapterIsSaving &&
                  sharedRendererProps?.adapter?.isLoading && (
                    <LoadingBadge>Loading...</LoadingBadge>
                  )}
              </TitleRow>
              <LastPublishedInfo
                displayWhenEmpty={true}
                emptyMessage="To publish and share, click the button below"
                lastPublishInfo={playingPublishInfo ?? allPublishInfo}
                // Only for the clip's own publish line. In Published Version
                // the language is already the mode dropdown's label
                // (`publishedVersionDetail`), so restating it here would say
                // the same thing twice (PLAT-11548 DS6).
                language={playingPublishInfo ? null : allPublishLanguage}
              />
            </TitleContainer>

            <LeftDivider />

            <TopContent data-testid="clip-preview-controls">
              {SHOW_DURATION_BADGE && (
                <DurationBadge color={getDurationColor(durationData.duration)}>
                  {durationData.formattedDuration}
                </DurationBadge>
              )}
              {/* Editing is the surface's resting state, so it is not
                  labelled — only the departure from it is. Watching a
                  published render is the temporary state, and its tag is also
                  the way out of it: the X returns to editing. */}
              {previewMode === 'rendered' && (
                <PublishedModeTag data-testid="clip-preview-published-tag">
                  <PreviewModeLabel>
                    Published Version
                    {!!publishedVersionDetail && (
                      <PreviewModeDetail>
                        {` · ${publishedVersionDetail}`}
                      </PreviewModeDetail>
                    )}
                  </PreviewModeLabel>
                  <ExitPublishedButton
                    aria-label="Exit Published Version"
                    data-testid="clip-preview-exit-published"
                    onClick={() => sessionStore.setPreviewMode('live')}
                  >
                    <CloseIcon width={10} height={10} />
                  </ExitPublishedButton>
                </PublishedModeTag>
              )}
              {/* Clip History: an older version is showing, read-only. */}
              {clipAdapter && <ClipVersionPreviewTag adapter={clipAdapter} />}
              {/* Reframe edits the live composition — hidden while viewing the
                  Published Version (an immutable render). */}
              {enableVideoClipEdit &&
                sharedRendererProps.isReady &&
                previewMode === 'live' &&
                !clipAdapter?.versionPreview.isPreviewing && (
                  <ReframeButton
                    data-testid="clip-preview-reframe-button"
                    onClick={() => setOpenReframeModal(true)}
                  >
                    <CropIcon fillColor="var(--text-secondary)" />
                    Reframe
                  </ReframeButton>
                )}
              {enableSafeZones && (
                <SafeZoneSlot
                  $active={sessionStore.selectedSafeZone !== 'none'}
                >
                  <SafeZoneDropdown
                    selection={sessionStore.selectedSafeZone}
                    selectedDimension={selectedDimension}
                    onSelect={onSelectSafeZone}
                    disabled={!sharedRendererProps.isReady}
                    disabledTooltip={SAFE_ZONE_STRINGS.dropdown.disabledLoading}
                    isVariantActive={key => sessionStore.safeZoneVariant(key)}
                    onToggleVariant={onToggleSafeZoneVariant}
                  />
                </SafeZoneSlot>
              )}
              {enableSharing && contentStudioModalEnableAnalytics && (
                <SocialShareStatus
                  showOnlyShared={true}
                  sharedContents={clipSharedContents}
                />
              )}
              <RightControls>
                {/* PLAT-11877 — Voice / Music for a dubbed language, put here
                    by the transcript bridge (it owns the clip's composition). */}
                <PlayerSettingsSlot />
                <Dimensions data-testid="clip-preview-aspect-ratio">
                  <ClipDimensionsDropdown
                    dimensions={allFormats}
                    selectedDimension={selectedDimension}
                    onSelectDimension={onSelectDimension}
                    publishingDimensions={publishingDimensions}
                    localPublish={
                      publishingDimension
                        ? {
                            aspectRatio: publishingDimension,
                            startTime: publishStartTime,
                            by: userIdentity.currentEditor,
                          }
                        : undefined
                    }
                    disabled={!sharedRendererProps.isReady}
                    disabledTooltip="Loading preview..."
                  />
                </Dimensions>
              </RightControls>
            </TopContent>

            <PreviewArea>
              <Stage $fullscreen={isPseudoFullscreen}>
                <VideoPlayer
                  ref={setVideoPlayerRef}
                  data-testid="clip-preview-player"
                  $aspectRatio={
                    previewMode === 'live'
                      ? liveAspectRatio
                      : cssAspectRatio(selectedDimension)
                  }
                >
                  {/* PLAT-11288 — the drawing surface. Inert (pointer-events:
                      none) until a tool is armed, so with no tool selected the
                      canvas behaves exactly as it does today. */}
                  {contentStudioEnableCommentDrawing && (
                    <ClipAnnotationLayer
                      playerElement={videoPlayerElement}
                      tool={annotations.tool}
                      color={annotations.color}
                      width={annotations.width}
                      strokes={visibleStrokes}
                      onStrokesChange={annotations.setStrokes}
                      // A mark belongs to one frame, so stop the picture first.
                      onDrawStart={unifiedPlayback.pause}
                    />
                  )}
                  {/* Safe-zone guides are hidden while drawing: they compete
                      for the same pixels, and their picker's z-index already
                      collides with the canvas action layer (see clipPreviewZ). */}
                  {enableSafeZones && !isPseudoFullscreen && !isAnnotating && (
                    <SafeZoneOverlay
                      selection={sessionStore.selectedSafeZone}
                      aspectRatio={selectedDimension}
                      rendererElement={videoPlayerElement}
                      variants={sessionStore.safeZoneVariantState}
                      onToggleVariant={onToggleSafeZoneVariant}
                      playback={unifiedPlayback}
                    />
                  )}
                  <ShareablePreview style={{ display: 'none' }}>
                    <ShareableImageView imageRef={imageRef} />
                  </ShareablePreview>
                  <ToggableContainer show={previewMode === 'live'}>
                    <CreatomateClipPreview
                      aspectRatio={liveAspectRatio}
                      sharedRendererProps={{
                        ...sharedRendererProps,
                        isLoading:
                          sharedRendererProps.isLoading ||
                          creatingDimension !== null,
                      }}
                      poster={effectivePosterUrl}
                      autoPlay={false}
                      processingVeil={audioFirst.processingVeil}
                    />
                    {audioFirst.nativeLocalPreviewUrl && (
                      <NativeLocalVideoPreview
                        src={audioFirst.nativeLocalPreviewUrl}
                        sourceTime={
                          (clipJson?.startTime ?? 0) +
                          (playbackState.seekTime ?? playbackState.currentTime)
                        }
                        isPlaying={playbackState.isPlaying}
                      />
                    )}
                    {/* PLAT-9968 — Canva-style canvas action menu (Edit /
                        Delete) over the iframe when a quick-editable
                        composition is selected. Portals to body so the
                        menu floats above the canvas without intercepting
                        iframe pointer events. */}
                    {/* Suppressed while a drawing tool is armed, so the
                        quick-edit pencil and the ink don't fight over the same
                        pixels (PLAT-11288). */}
                    {enableCompositionCanvasQuickEdit && !isAnnotating && (
                      <CompositionCanvasActionsLayer
                        rendererContainerRef={
                          sharedRendererProps.rendererContainerRef
                        }
                        adapter={sharedRendererProps.adapter ?? null}
                        editorStore={sharedRendererProps.editorStore ?? null}
                        videoSourceStore={
                          sharedRendererProps.videoSourceStore ?? null
                        }
                        // PLAT-10191 — flush the post-delete editor state via the
                        // clip adapter's background autosave so a canvas delete
                        // persists like an add (the command's own autosave
                        // snapshots the pre-delete state).
                        onPersistAfterMutation={() =>
                          void sharedRendererProps.adapter?.saveStoryAndVideo(
                            false,
                            true,
                            false,
                            true,
                          )
                        }
                        // PLAT-12102 DS5 — the bars below replace the chip for
                        // the elements they cover.
                        barsMounted={toolbarsMounted && !!toolbarHost}
                        quickEditHandleRef={quickEditHandleRef}
                      />
                    )}
                    {toolbarsMounted && toolbarHost && toolbarStoreScope && (
                      <StoreContext.Provider value={toolbarStoreScope}>
                        <FloatingToolbarHostProvider host={toolbarHost}>
                          <TextFloatingToolbar />
                          <ImageFloatingToolbar />
                          <VideoFloatingToolbar />
                          <CaptionsFloatingToolbar />
                        </FloatingToolbarHostProvider>
                      </StoreContext.Provider>
                    )}
                  </ToggableContainer>
                  {previewMode === 'rendered' && (
                    <>
                      {publishedSource ? (
                        <MuxPlayer
                          ref={playerRef}
                          style={playerStyle}
                          onTimeUpdate={updateTimeOnPublishedVideo}
                          onLoadedMetadata={onPublishedLoadedMetadata}
                          onPlay={onPublishedPlay}
                          onPause={onPublishedPause}
                          nohotkeys
                          streamType="on-demand"
                          playbackId={playbackId}
                          src={renderedVideoSrc}
                          poster={effectivePosterUrl}
                          volume={volumeControls.volume}
                          muted={volumeControls.muted}
                        />
                      ) : publishedResolving ? (
                        <LoadingState message="Loading published version…" />
                      ) : reelCuts?.length > 1 ? (
                        <MuxPlayerMultiClip
                          autoPlay={false}
                          cuts={reelCuts}
                          poster={effectivePosterUrl}
                          muxPlaybackId={playbackId}
                          aspectRatio={selectedDimension as AspectRatio}
                          // PLAT-11042: usable-timer video gate — these
                          // wrappers manage their own duration/time state,
                          // so only the readiness signal is routed up.
                          onLoadedMetadata={() => setPublishedReady(true)}
                          volume={volumeControls.volume}
                          muted={volumeControls.muted}
                        />
                      ) : (
                        <MuxPlayerClip
                          onLoadedMetadata={() => setPublishedReady(true)}
                          clip={{
                            startTime:
                              clipJson?.startTime ??
                              clipElement?.trim_start ??
                              0,
                            duration:
                              clipJson?.duration ?? clipElement?.duration ?? 0,
                            muxPlaybackId: playbackId,
                            src: clipSource,
                            poster: effectivePosterUrl,
                            autoPlay: false,
                          }}
                          aspectRatio={selectedDimension as AspectRatio}
                          volume={volumeControls.volume}
                          muted={volumeControls.muted}
                        />
                      )}
                    </>
                  )}
                </VideoPlayer>
              </Stage>
              {previewMode === 'live' &&
                (() => {
                  const bar = (
                    <ClipPreviewControlBar
                      playbackState={unifiedPlayback}
                      isReady={unifiedPlayback.isReady}
                      play={unifiedPlayback.play}
                      pause={unifiedPlayback.pause}
                      setTime={unifiedPlayback.setTime}
                      setSeekTime={unifiedPlayback.setSeekTime}
                      setIsSeeking={unifiedPlayback.setIsSeeking}
                      framerate={
                        sharedRendererProps.adapter?.timelineStore?.framerate
                      }
                      onToggleFullscreen={toggleFullscreen}
                      isFullscreen={isPseudoFullscreen}
                      commentMarkers={commentMarkers}
                      onCommentJump={handleCommentJump}
                      onCommentMove={handleCommentMove}
                      thumbnailPlaybackId={hoverThumbnailPlaybackId}
                      volumeControls={volumeControls}
                      sceneStrip={sceneStrip}
                    />
                  );
                  return isPseudoFullscreen
                    ? createPortal(bar, document.body)
                    : bar;
                })()}
              {previewMode === 'rendered' &&
                !!publishedSource &&
                (() => {
                  const bar = (
                    <ClipPreviewControlBar
                      playbackState={unifiedPlayback}
                      isReady={unifiedPlayback.isReady}
                      play={unifiedPlayback.play}
                      pause={unifiedPlayback.pause}
                      setTime={unifiedPlayback.setTime}
                      setSeekTime={unifiedPlayback.setSeekTime}
                      setIsSeeking={unifiedPlayback.setIsSeeking}
                      onToggleFullscreen={toggleFullscreen}
                      isFullscreen={isPseudoFullscreen}
                      commentMarkers={commentMarkers}
                      onCommentJump={handleCommentJump}
                      onCommentMove={handleCommentMove}
                      thumbnailPlaybackId={hoverThumbnailPlaybackId}
                      volumeControls={volumeControls}
                      // Scenes describe the clip as edited, which a published
                      // render need not match — down to its duration, which
                      // they are scaled by. The rail is simply unbroken here.
                    />
                  );
                  return isPseudoFullscreen
                    ? createPortal(bar, document.body)
                    : bar;
                })()}
              {/* Reel/multi-clip published renders use MuxPlayer's own in-stage
                  controls, so no external ClipPreviewControlBar renders here.
                  Reserve its exact height so the modal doesn't shrink when
                  switching from Edit Mode (which always shows the bar). */}
              {previewMode === 'rendered' && !publishedSource && (
                <ControlBarSpacer
                  $withMarkers={commentMarkers.length > 0}
                  aria-hidden="true"
                />
              )}
              {isPseudoFullscreen &&
                createPortal(
                  <>
                    <FullscreenCloseButton
                      type="button"
                      onClick={() => setIsPseudoFullscreen(false)}
                      aria-label="Exit fullscreen"
                      data-tooltip-id="clip-exit-fullscreen-tooltip"
                      data-tooltip-content="Exit fullscreen"
                    >
                      <CloseIcon
                        width={18}
                        height={18}
                        strokeColor="currentColor"
                      />
                    </FullscreenCloseButton>
                    <Tooltip
                      id="clip-exit-fullscreen-tooltip"
                      style={COMPACT_TOOLTIP_STYLE}
                      place="bottom"
                      positionStrategy="fixed"
                    />
                  </>,
                  document.body,
                )}
            </PreviewArea>
            <ButtonGroup>
              {enableVideoClipEdit && enableCreatorStudioNav && (
                <FooterSecondaryButton
                  data-testid="clip-preview-edit-in-studio"
                  disabled={isLeavingForCreator}
                  onClick={handleEditInCreator}
                >
                  <PencilIcon
                    width="16"
                    height="16"
                    strokeColor="currentColor"
                  />
                  {isLeavingForCreator ? 'Saving…' : 'Edit in Creator Studio'}
                </FooterSecondaryButton>
              )}
              {contentStudioModalEnableThumbnail &&
                !enableTranslationReviewerMode && (
                  <FooterSecondaryButton
                    onClick={() => togglePhotosModal(true)}
                  >
                    <ThumbnailIcon
                      width="16"
                      height="16"
                      strokeColor="currentColor"
                    />
                    Thumbnail
                  </FooterSecondaryButton>
                )}
              {!enableTranslationReviewerMode && (
                <>
                  <ClipPublishShareButton
                    ref={exportButtonRef}
                    blocked={publishGate.isBlocked || isAwaitingExpansion}
                    tooltip={
                      publishGate.tooltip ??
                      (isAwaitingExpansion
                        ? 'Clip is still loading'
                        : undefined)
                    }
                    onClick={() => setExportOpen(o => !o)}
                  />
                  {exportOpen && (
                    <ExportMenu
                      open
                      onClose={() => setExportOpen(false)}
                      triggerRef={exportButtonRef}
                      mainClip={mainClip as Video}
                      // Re-read at dispatch. Publishing first flushes this
                      // screen's live timeline into the session store, which
                      // replaces the variant, so the clip captured above is a
                      // different object from the one being published — and the
                      // panel closes itself before React could re-render it.
                      getMainClip={() => sessionStore.mainClip as Video}
                      selectedDimension={selectedDimension}
                      onPublish={() => setActiveTab('downloads')}
                      defaultLanguage={sessionStore.selectedLanguage}
                      videoId={activeVideoId}
                      adapter={sharedRendererProps.adapter}
                      onViewDownloads={() => setActiveTab('downloads')}
                    />
                  )}
                </>
              )}
            </ButtonGroup>

            {/* Saving a photo is scoped to this panel, so the indicator sits
                inline under its buttons rather than dimming the whole app.
                The guard was `savingStock || (savingQuote && …)`, which
                evaluates to `true` when savingStock is set — so the stock
                branch never rendered and its copy was dead. */}
            {(savingStock || savingQuote) && (
              <LoadingState
                size="sm"
                message={
                  savingQuote ? 'Saving quote photo…' : 'Saving stock photo…'
                }
              />
            )}

            {openReframeModal &&
              sharedRendererProps.videoSourceStore &&
              sharedRendererProps.editorStore && (
                <ClipPreviewReframeModal
                  isOpen={openReframeModal}
                  onClose={() => setOpenReframeModal(false)}
                  editorStore={sharedRendererProps.editorStore!}
                  videoSourceStore={sharedRendererProps.videoSourceStore!}
                  renderer={sharedRendererProps.renderer}
                  isReady={sharedRendererProps.isReady}
                  aspectRatio={selectedDimension}
                  initialTime={sharedRendererProps.adapter?.time ?? 0}
                  showSceneStrip={contentStudioModalEnableScenesStrip}
                />
              )}

            {contentStudioModalEnableThumbnail && showPhotosModal && (
              <Modal
                isOpen={true}
                onClose={() => togglePhotosModal(false)}
                paddingHorizontal="0"
              >
                <PhotoModal
                  TopComponent={props => (
                    <PhotoModalTop
                      {...props}
                      replaceAction={onReplaceAction}
                      title="Thumbnail"
                      isSelected={!!selectedImage}
                      selectedImage={selectedImage}
                    />
                  )}
                  otherFields={['stock', 'quotes']}
                  onCloseSelf={() => togglePhotosModal(false)}
                  selectedImageUrl={selectedImage?.url || null}
                  setSelectedImage={setSelectedImage}
                  setSearchText={setSearchText}
                  searchText={searchText}
                />
              </Modal>
            )}
          </LeftPane>

          <RightPane>
            <ShareModalTabs
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              hidePosts={tabVisibility.hidden.has('posts')}
              hideTranscript={tabVisibility.hidden.has('transcript')}
              hideLanguages={tabVisibility.hidden.has('languages')}
              hideReelEditor={tabVisibility.hidden.has('reelEditor')}
              hideComments={tabVisibility.hidden.has('comments')}
              hideTemplates={tabVisibility.hidden.has('templates')}
              hideDownloads={tabVisibility.hidden.has('downloads')}
              showHistory={!tabVisibility.hidden.has('history')}
            >
              {tab => {
                return (
                  <>
                    {contentStudioModalEnableShare && (
                      <ToggableContainer show={tab === 'posts'}>
                        <ShareModalPostsContent
                          clip={activeClipForPosts}
                          selectedDimension={selectedDimension}
                          hideShareButton={!contentStudioEnableClipShare}
                          posterUrl={effectivePosterUrl}
                          imageRef={imageRef}
                        />
                      </ToggableContainer>
                    )}
                    <ClipVersionPreviewGuard adapter={clipAdapter}>
                      <ToggableContainer
                        show={tab === 'templates'}
                        onClickCapture={() =>
                          sessionStore.setPreviewMode('live')
                        }
                      >
                        <TemplatesContainer>
                          {audioFirst.templatesLocked ? (
                            // PLAT-11445: templates preview branding over the
                            // real video, which does not exist until renditions
                            // land — and the provider's timeline rewrites over an
                            // audio-first composition spin the tab. Locked until
                            // the composition is on video.
                            <TemplatesLockedNote data-testid="clip-templates-locked">
                              Video is still processing — templates unlock when
                              the preview is ready.
                            </TemplatesLockedNote>
                          ) : (
                            <ClipTemplatesProvider
                              clip={activeClip}
                              story={story}
                              editorStore={sharedRendererProps.editorStore}
                              videoSourceStore={
                                sharedRendererProps.videoSourceStore
                              }
                              isPreviewLoading={
                                sharedRendererProps.isPreviewLoading
                              }
                              adapter={sharedRendererProps.adapter}
                            />
                          )}
                        </TemplatesContainer>
                      </ToggableContainer>
                    </ClipVersionPreviewGuard>
                    <ToggableContainer
                      show={tab === 'transcript'}
                      onClickCapture={() => sessionStore.setPreviewMode('live')}
                    >
                      <ClipTranscriptBridge
                        editorStore={sharedRendererProps.editorStore}
                        adapter={sharedRendererProps.adapter}
                        selectedLanguage={sessionStore.selectedLanguage}
                        onSelectedLanguageChange={
                          sessionStore.setSelectedLanguage
                        }
                        isVisible={tab === 'transcript'}
                        // PLAT-11877 — the Languages tab's pane: the bridge
                        // renders the language list into it (a portal, so it
                        // keeps the clip-scoped stores), and its globe and
                        // rows move between the two tabs.
                        languagesPane={
                          tabVisibility.hidden.has('languages')
                            ? null
                            : languagesPane
                        }
                        onOpenLanguages={openLanguagesTab}
                        onCloseLanguages={openTranscriptTab}
                        story={story}
                        sessionStore={sessionStore}
                        clip={clip}
                        // Only when there is a Reel Editor tab to open —
                        // otherwise the heading offers no way back
                        // (PLAT-11163, the mirror of the guard below).
                        onOpenSectionInReelEditor={
                          enableBasicReelCreation
                            ? openSectionInReelEditor
                            : undefined
                        }
                      />
                    </ToggableContainer>
                    <ToggableContainer show={tab === 'languages'}>
                      <LanguagesPane
                        ref={setLanguagesPane}
                        data-testid="clip-preview-languages-pane"
                      />
                    </ToggableContainer>
                    {enableBasicReelCreation && (
                      <ClipVersionPreviewGuard adapter={clipAdapter}>
                        <ToggableContainer
                          show={tab === 'reelEditor'}
                          onClickCapture={() =>
                            sessionStore.setPreviewMode('live')
                          }
                        >
                          {/* PLAT-11249 — Clips and Reels are separate content
                              types: a plain clip gets no reel editor to poke
                              at, it gets the explicit Clip-to-Reel action. The
                              conversion saves a reel of one Section and opens
                              it, which lands right back on this tab — as a
                              Reel this time. */}
                          {isReelClip ? (
                            <ReelEditorPanel
                              editorStore={sharedRendererProps.editorStore}
                              story={story}
                              datoClientStore={datoClientStore}
                              videoSourceStore={
                                sharedRendererProps.videoSourceStore
                              }
                              adapter={sharedRendererProps.adapter}
                              clip={clip}
                              topicSpans={topicSpans}
                              sourceElements={sourceElements}
                              // PLAT-11146 — the panel outlives its tab, so its
                              // undo hotkeys read `tab`: the same expression the
                              // `show` above reads, bound exactly when visible.
                              isVisible={tab === 'reelEditor'}
                              // Only when there is a transcript tab to open —
                              // otherwise the item stands down (PLAT-11061).
                              onEditSectionTranscript={
                                tabVisibility.hidden.has('transcript')
                                  ? undefined
                                  : editSectionTranscript
                              }
                              revealSectionId={
                                tab === 'reelEditor'
                                  ? pendingReelRevealSectionId
                                  : null
                              }
                              onRevealHandled={handleReelRevealHandled}
                            />
                          ) : (
                            <ReelEditorEmptyState data-testid="reel-editor-convert-empty">
                              <ReelEditorEmptyHint>
                                This clip is not a reel yet. Reels arrange clips
                                into a running order you can reorder and
                                chapter.
                              </ReelEditorEmptyHint>
                              <CreateReelFromClipButton
                                type="button"
                                disabled={isSavingReelFromClip}
                                data-testid="create-reel-from-clip"
                                onClick={() => void createReelFromClip(clip)}
                              >
                                {isSavingReelFromClip
                                  ? 'Creating…'
                                  : 'Create Reel from Clip'}
                              </CreateReelFromClipButton>
                            </ReelEditorEmptyState>
                          )}
                        </ToggableContainer>
                      </ClipVersionPreviewGuard>
                    )}
                    {contentStudioModalEnableComments && (
                      <ToggableContainer show={tab === 'comments'}>
                        {!activeVideoId ? (
                          <CommentsTabEmptyState>
                            Comments will be available once this format is
                            saved.
                          </CommentsTabEmptyState>
                        ) : (
                          <CommentsContainer>
                            {/* Chat-style: thread list fills the space, the
                                composer is pinned at the bottom (bottom-aligned
                                with the modal's Publish & Share action). */}
                            <CommentList
                              selectedDrawingId={shownDrawing?.id ?? null}
                              onSelectDrawing={setShownDrawing}
                              store={commentStore}
                              playback={unifiedPlayback}
                              framerate={
                                sharedRendererProps.adapter?.timelineStore
                                  ?.framerate
                              }
                              mentionPeople={mentionPeople}
                              personColors={personColors}
                            />
                            <CommentComposer
                              store={commentStore}
                              playback={unifiedPlayback}
                              mentionPeople={mentionPeople}
                              personColors={personColors}
                              // Passing the controller is what shows the tools
                              // row; omitting it when the flag is off keeps the
                              // composer flag-agnostic (PLAT-11289).
                              annotations={
                                contentStudioEnableCommentDrawing
                                  ? annotations
                                  : undefined
                              }
                            />
                          </CommentsContainer>
                        )}
                      </ToggableContainer>
                    )}
                    {!tabVisibility.hidden.has('history') && clipAdapter && (
                      <ToggableContainer show={tab === 'history'}>
                        <ClipVersionHistoryPanel
                          adapter={clipAdapter}
                          isActive={tab === 'history'}
                        />
                      </ToggableContainer>
                    )}
                    <ToggableContainer show={tab === 'downloads'}>
                      <DownloadsTab
                        videoId={activeVideoId}
                        videoTitle={clip.title}
                        adapter={sharedRendererProps.adapter}
                        // A translated publish never reaches
                        // `videoFilePrimary`, so its row is the only handle on
                        // it — clicking one plays that exact render and
                        // switches to Published Version (PLAT-11548).
                        onPreviewRecord={record =>
                          sessionStore.pinPublishRecord(record.id)
                        }
                        previewingRecordId={sessionStore.pinnedPublishRecordId}
                      />
                    </ToggableContainer>
                  </>
                );
              }}
            </ShareModalTabs>
          </RightPane>

          {/* Photo replacement modal for clip logo/image replacement */}
          {sharedRendererProps.adapter?.openPhotoElementReplacementModal
            ?.element && (
            <ClipPhotoReplacementModal adapter={sharedRendererProps.adapter} />
          )}
        </Main>
      </>
    );
  },
);

/**
 * Lightweight, non-functional view of the modal content shown while a clip
 * still has a temporary ID (background save in progress). Mirrors the real
 * modal layout (title, duration badge, poster image, current AR badge) but
 * does NOT initialise the Creatomate renderer, adapter, or any heavy hooks.
 * Once `replaceOptimisticVideoId` swaps the temp ID for a real one, the
 * parent unmounts this and mounts FullClipModalContent instead.
 */
const TempClipModalContent = observer(({ clip }: { clip: Props['clip'] }) => {
  const durationData = getFormattedDurationForStoryVideo(clip);
  const thumbnailUrl = getClipThumbnail(clip);
  const userIdentity = useUserIdentity();
  const { contentStudioModalEnableComments } = useFlagsCombination(
    userIdentity.currentRole,
  );
  const [activeTab, setActiveTab] = useState<ShareModalTabType>('transcript');

  const selectedDimension = clip.aspectRatio || AspectRatio.AR_16_9;
  const existingFormats = getAvailableClipFormats(clip);
  const allFormats: DimensionItem[] = useMemo(() => {
    const existingArs = new Set(existingFormats.map(f => f.aspectRatio));
    const existing: DimensionItem[] = existingFormats.map(f => ({
      aspectRatio: f.aspectRatio,
      isPublished: f.isPublished,
      isNew: false,
    }));
    const missing: DimensionItem[] = Object.values(AspectRatio)
      .filter(ar => !existingArs.has(ar))
      .map(ar => ({
        aspectRatio: ar,
        isPublished: false,
        isNew: true,
      }));
    const all = [...existing, ...missing];
    const canonicalOrder = Object.values(AspectRatio);
    all.sort(
      (a, b) =>
        canonicalOrder.indexOf(a.aspectRatio as AspectRatio) -
        canonicalOrder.indexOf(b.aspectRatio as AspectRatio),
    );
    return all;
  }, [existingFormats]);

  return (
    <Main data-testid="clip-share-modal">
      <LeftPane>
        <TitleContainer>
          <TitleRow>
            {/* The skeleton's heading is static: this clip is mid-save and has
                no record to rename yet, so it is not `ClipPreviewTitle`. */}
            <SkeletonTitle>{clip?.title || ''}</SkeletonTitle>
            {/* The clip record is still being written, so this is always the
                in-flight state of the same autosave chip the full modal shows. */}
            <AutosaveStatusIndicator status="saving" />
          </TitleRow>
          <LastPublishedInfo
            displayWhenEmpty={true}
            emptyMessage="To publish and share, click the button below"
            lastPublishInfo={[]}
          />
        </TitleContainer>

        <LeftDivider />

        <TopContent>
          {SHOW_DURATION_BADGE && (
            <DurationBadge color={getDurationColor(durationData.duration)}>
              {durationData.formattedDuration}
            </DurationBadge>
          )}
          {durationData.duration != null && durationData.duration > 0 && (
            <PlaybackTime>
              {formatPlaybackTime(0)} /{' '}
              {formatPlaybackTime(durationData.duration)}
            </PlaybackTime>
          )}
          <RightControls>
            <Dimensions>
              <ClipDimensionsDropdown
                dimensions={allFormats}
                selectedDimension={selectedDimension}
                onSelectDimension={() => {}}
                disabled
                disabledTooltip="Saving clip..."
              />
            </Dimensions>
          </RightControls>
        </TopContent>

        <PreviewArea>
          <Stage>
            <VideoPlayer
              data-testid="clip-preview-player"
              $aspectRatio={cssAspectRatio(selectedDimension)}
            >
              <TempVideoContainer>
                {thumbnailUrl ? (
                  <TempPosterImage src={thumbnailUrl} alt="Clip preview" />
                ) : (
                  <TempPosterPlaceholder />
                )}
                <LoadingOverlay message="Loading video…" />
              </TempVideoContainer>
            </VideoPlayer>
          </Stage>
        </PreviewArea>
        <ButtonGroup>
          <FooterSecondaryButton
            data-testid="clip-preview-edit-in-studio"
            disabled
          >
            <PencilIcon width="16" height="16" strokeColor="currentColor" />
            Edit in Creator Studio
          </FooterSecondaryButton>
          <ClipPublishShareButton blocked onClick={() => {}} />
        </ButtonGroup>
      </LeftPane>

      <RightPane>
        <ShareModalTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          hideComments={!contentStudioModalEnableComments}
        >
          {tab => (
            <TempTabContent>
              <LoadingState message={TEMP_TAB_LOADING_MESSAGE[tab]} />
            </TempTabContent>
          )}
        </ShareModalTabs>
      </RightPane>
    </Main>
  );
});

/**
 * Outer shell for the clip share modal. Handles the Modal wrapper, escape key,
 * and conditionally renders either TempClipModalContent (dumb UI while clip
 * has a temporary ID) or FullClipModalContent (full interactive UI).
 */
const SocialClipShareModal = observer((props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const { publishTrackingStore } = useStore();
  const { clip, onClose, onCommentCountChange } = props;
  const hasTempId = isTempClipId(clip.id);
  const savingRef = useRef(false);
  const beforeCloseRef = useRef<(() => Promise<void>) | null>(null);
  const closingRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);

  // Mid-publish the progress strip sits right beside this modal's close X,
  // which makes the X read as "cancel the publish". Say outright that it
  // doesn't. Only while a publish is actually running — not for a finished
  // batch still on screen (PLAT-10947).
  const closeHint = publishTrackingStore.isPublishInProgress
    ? "Close — this won't cancel publishing"
    : undefined;

  const handleClose = async () => {
    if (savingRef.current || closingRef.current) return;
    if (videoCreator.showRefreshStoryForSocialProfile) {
      runInAction(
        () => (videoCreator.showRefreshStoryForSocialProfile = false),
      );
      return;
    }
    // PLAT-11911: an edit still in the auto-save debounce would otherwise
    // save after close, racing a Creator Studio load of the same clip.
    closingRef.current = true;
    try {
      await beforeCloseRef.current?.();
    } catch {
      // The autosave chip shows the failed save; stay open.
      return;
    } finally {
      closingRef.current = false;
    }
    const tempImageContainer = document.getElementById(
      'placeholder-shareable-image',
    );
    if (tempImageContainer) tempImageContainer.remove();
    onClose();
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <PlayerSettingsSlotProvider>
      <Modal
        isOpen
        allowPropagation
        onClose={handleClose}
        paddingVertical="0"
        paddingHorizontal="0"
        customWidth="max(min(80%, 1250px), 850px)"
        alwaysShowCancel={videoCreator.showRefreshStoryForSocialProfile}
        closeTooltip={isSaving ? 'Saving, wait a moment' : undefined}
        closeHint={closeHint}
      >
        {hasTempId ? (
          <TempClipModalContent clip={clip} />
        ) : (
          <>
            {/* Publish progress, laid into the modal's top-right header band
              (left of Modal's close X) instead of floating over it — PLAT-10947.
              A direct child of Modal's `position: relative` Content, which is
              what the strip anchors to. While mounted it stands the global
              `PublishProgressDrawer` down; closing the modal hands the surface
              back. Only on the full-clip path: the temp-clip placeholder has no
              header band to sit in, so a publish there keeps the drawer.
              Scoped to this clip (PLAT-11395): a publish of some other clip
              stays in the floating drawer rather than appearing in this clip's
              header, where it reads as this clip publishing. */}
            {clip.id && <PublishProgressHeaderStrip clipId={clip.id} />}
            <FullClipModalContent
              clip={clip}
              savingRef={savingRef}
              beforeCloseRef={beforeCloseRef}
              onSavingChange={setIsSaving}
              onCommentCountChange={onCommentCountChange}
            />
          </>
        )}
      </Modal>
    </PlayerSettingsSlotProvider>
  );
});

export default SocialClipShareModal;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  h5 {
    margin: 0;
  }

  h5 {
    font-size: 12px;
    font-weight: 500;
    color: var(--text-muted);
    margin-bottom: 8px;
  }
`;

const DurationBadge = styled.div<{ color?: string }>`
  display: flex;
  align-items: center;
  padding: 0 8px;
  background-color: ${props => props.color || ''};
  color: black;
  width: fit-content;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  height: 28px;
`;

const TopContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ReframeButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: none;
  border-radius: var(--radius-button);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  background-color: transparent;
  cursor: pointer;
  user-select: none;
  transition: all 150ms ease-out;

  svg path {
    fill: var(--text-secondary);
    transition: fill 150ms ease-out;
  }

  &:hover {
    background-color: var(--surface-elevated);
    color: var(--foreground);

    svg path {
      fill: var(--foreground);
    }
  }

  &:active {
    background-color: var(--gray-700);
    scale: 0.98;
  }
`;

const Dimensions = styled.div``;

// Right-aligned group in the top control row holding the editing-workflow
// actions: "Edit in Creator Studio" next to the aspect-ratio selector.
const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
`;

// Hide the Safe Zones dropdown on narrow viewports so the aspect-ratio
// control (Dimensions) — which must always be reachable — never gets
// pushed off-screen.
const SafeZoneSlot = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 6px;
  border-radius: 6px;
  background: ${({ $active }) =>
    $active ? 'var(--surface-elevated, #2a2a2a)' : 'transparent'};
  transition: background-color 150ms ease-out;

  @media (max-width: 1150px) {
    display: none;
  }
`;

const LeftDivider = styled.div`
  height: 1px;
  width: 100%;
  background: var(--border);
`;

const PreviewArea = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

// Matches ClipPreviewControlBar's exact height (8px top padding + 4px scrubber +
// 6px gap + 36px controls row + 8px bottom padding = 62px). Reserved in the one
// preview state that has no external control bar, so toggling Edit Mode /
// Published Version never changes the modal height. When pinned comments exist
// the live bar also renders the marker row (+10px row + 6px gap = 78px), so the
// spacer grows to match — otherwise switching to the multi-clip render shrinks
// the modal by 16px. Scenes add nothing here: they are cut into the rail
// itself rather than occupying a row of their own, and the hovered segment
// grows by overflowing the rail rather than by resizing it (PLAT-11504).
const ControlBarSpacer = styled.div<{
  $withMarkers?: boolean;
}>`
  height: ${props => (props.$withMarkers ? 78 : 62)}px;
  flex-shrink: 0;
`;

const FullscreenCloseButton = styled.button`
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: ${CLIP_PREVIEW_Z.modalChrome};
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  color: var(--foreground);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  transition:
    background-color 0.15s ease,
    transform 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.75);
    transform: scale(1.05);
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }
`;

const Stage = styled.div<{ $fullscreen?: boolean }>`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 10px 10px 0 0;
  background: var(--background);

  ${props =>
    props.$fullscreen &&
    css`
      position: fixed;
      inset: 0;
      width: 100vw;
      height: 100vh;
      aspect-ratio: auto;
      border-radius: 0;
      z-index: ${CLIP_PREVIEW_Z.modalFullscreenBody};
    `}
`;

const VideoPlayer = styled.div<{ $aspectRatio?: string }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  max-width: 100%;
  aspect-ratio: ${props => props.$aspectRatio ?? '16 / 9'};
`;

const ButtonGroup = styled.div`
  display: flex;
  margin-left: 0;
  margin-top: auto;
  justify-content: end;
  gap: 10px;
`;

// Design-system Secondary button for the footer actions, sized to share the
// row with ClipPublishShareButton (48px tall, at most half the row).
const FooterSecondaryButton = styled(SecondaryButton)`
  flex: 1;
  max-width: 50%;
  height: 48px;
  /* Three actions can share the row, so let long labels wrap rather than clip. */
  white-space: normal;
  text-align: center;
`;

const ShareablePreview = styled.div`
  position: absolute;
  top: -80px;
  left: 1px;
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  margin: 0 auto;
`;

const ToggableContainer = styled.div<{ show: boolean }>`
  display: ${props => (props.show ? 'flex' : 'none')};
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  padding-right: 0;
  position: relative;
`;

// Layout for the comments tab: composer on top, thread list (PLAT-10302) below.
const CommentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md, 16px);
  width: 100%;
  flex: 1;
  min-height: 0;
`;

// Shown while the clip is unsaved (no slug/hash to key comments by). Once
// saved, the "no comments yet" case is owned by CommentList.
const CommentsTabEmptyState = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 24px;
  color: var(--text-muted);
  font-size: 14px;
  text-align: center;
  line-height: 1.5;
`;

const Main = styled.div`
  position: relative;
  color: var(--text-secondary);
  /* Single source of the modal's content margin: 24px on all sides — the
     Modal shell contributes 0 (paddingHorizontal/Vertical="0"), horizontal
     comes from here, vertical from LeftPane's margin: 24px 0. */
  padding: 0 24px;
  text-align: left;
  width: 100%;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 20px;
`;

const LeftPane = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin: 24px 0;
  min-width: 0;
  overflow: hidden;
`;

/* PLAT-11877 — the Languages tab's pane; its content is portaled in. */
const LanguagesPane = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-height: 0;
`;

const RightPane = styled.div`
  position: relative;

  > * {
    position: absolute;
    inset: 0;
  }
`;

const TemplatesLockedNote = styled.p`
  margin: 0;
  padding: 24px 16px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-muted);
  text-align: center;
`;

const TemplatesContainer = styled.div`
  width: calc(100% - 16px);
  height: 100%;
`;

const PlaybackTime = styled.span`
  color: var(--text-muted);
  font-size: 13px;
  font-family: 'helvetica neue', 'segoe ui', roboto, arial, sans-serif;
  white-space: nowrap;

  @media (max-width: 1150px) {
    display: none;
  }
`;

/* Trigger label matching SafeZoneDropdown's "Preview" — the mode dropdown and
   its siblings on the control bar read as one visual family. */
const PreviewModeLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  white-space: nowrap;
  margin-right: 4px;
`;

/**
 * The "which version" half of the mode label — muted and normal-weight so the
 * trigger reads as "Published Version" with a qualifier rather than as two
 * labels fighting. Not gold: it states a fact, and an accent here shouted
 * (PLAT-11548 review).
 */
const PreviewModeDetail = styled.span`
  font-weight: var(--font-weight-normal);
  color: var(--text-muted);
`;

/* Keeps the footprint the mode dropdown had on the control bar, minus the
   press: the label states what is playing, and only the X is clickable. */
const PublishedModeTag = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  width: max-content;
  padding: 2px 2px 2px 8px;
  border-radius: var(--radius);
`;

const ExitPublishedButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  background: transparent;
  border: none;
  padding: var(--spacing-xs);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: var(--foreground);
    background-color: var(--surface-elevated);
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/* PLAT-11249 — the Reel Editor tab of a plain clip: empty on purpose, with
   the one action that changes what this content *is* in the middle. */
const ReelEditorEmptyState = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  padding: var(--spacing-xl);
  text-align: center;
`;

const ReelEditorEmptyHint = styled.p`
  margin: 0;
  max-width: 300px;
  color: var(--text-muted);
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
`;

/* Primary green: it is the pane's one action, and it makes something. Small —
   an empty pane makes any button read bigger than it is. */
const CreateReelFromClipButton = styled.button`
  height: var(--btn-h-sm);
  padding: 0 var(--btn-px-sm);
  border: none;
  border-radius: var(--radius-button);
  background: var(--primary);
  color: var(--primary-foreground);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: background var(--transition-fast);

  &:hover:not(:disabled) {
    background: var(--primary-hover);
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

/* The shared Reel / Clip identity pill (PLAT-11249 extracted it for the clip
   grid's cards too), height-locked for this one host: under the title's own
   line box (a 20px font runs at least 26px tall), so the pill can never be
   the title row's tallest element — which is what kept nudging the pane's
   divider out of line with the right pane's. The row's height is the title's,
   exactly as it was before the badge existed. */
const ClipTypeBadge = styled(ClipTypeBadgeBase)`
  height: 22px;
  padding: 0 10px;
  line-height: 1;
`;

const SkeletonTitle = styled.h2`
  width: 100%;
  font-size: 20px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
  color: var(--foreground);
`;

/* PLAT-11654 — mirrors BlogView's SaveStatusGroup: cloud icon + status chip
   sit together where the yellow "Saving..." badge used to be. */
const SaveStatusGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

const LoadingBadge = styled.div`
  padding: 4px 10px;
  background-color: var(--accent);
  color: var(--accent-foreground);
  border-radius: 4px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: 1.4;
  white-space: nowrap;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.6;
    }
  }
`;

// --- Styled components for TempClipModalContent ---

const TempVideoContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid var(--border);
  background-color: #000;
`;

const TempPosterImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const TempPosterPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: var(--surface);
`;

const TEMP_TAB_LOADING_MESSAGE: Partial<Record<ShareModalTabType, string>> = {
  transcript: 'Loading transcript…',
  languages: 'Loading languages…',
  templates: 'Loading templates…',
  posts: 'Loading posts…',
  comments: 'Loading comments…',
};

const TempTabContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 40px;
`;
